/**
 * QUIZ V2 - Sprouties Cat Grass Recommendation Quiz
 * ==================================================
 * State machine, step rendering, recommendation engine,
 * cart API, Klaviyo, analytics.
 *
 * Architecture: Single state object, pure render functions,
 * event delegation, no framework.
 *
 * Steps (all individual pages):
 *   intro > cat_names > age > appetite > diet > coat >
 *   concerns > experience > anxiety > personality >
 *   analyzing > results
 */

(function () {
  'use strict';

  // ─── CONSTANTS ────────────────────────────────────────────

  var STEPS = {
    INTRO: 'intro',
    CAT_NAMES: 'cat_names',
    AGE: 'age',
    APPETITE: 'appetite',
    DIET: 'diet',
    COAT: 'coat',
    CONCERNS: 'concerns',
    EXPERIENCE: 'experience',
    ANXIETY: 'anxiety',
    PERSONALITY: 'personality',
    ANALYZING: 'analyzing',
    RESULTS: 'results'
  };

  // Order for navigation (per-cat steps repeat)
  var PER_CAT_STEPS = [
    STEPS.AGE, STEPS.APPETITE, STEPS.DIET, STEPS.COAT,
    STEPS.CONCERNS, STEPS.EXPERIENCE, STEPS.ANXIETY,
    STEPS.PERSONALITY
  ];

  var PROGRESS_MAP = {
    cat_names: 8,
    age: 16,
    appetite: 25,
    diet: 34,
    coat: 43,
    concerns: 52,
    experience: 61,
    anxiety: 70,
    personality: 80,
    analyzing: 90
  };

  var CONCERNS_OPTIONS = [
    { value: 'hairballs', label: 'Hairballs', icon: '\uD83E\uDDF6' },
    { value: 'digestion', label: 'A sensitive stomach', icon: '\uD83D\uDC3E' },
    { value: 'boredom', label: 'Indoor boredom', icon: '\uD83D\uDE34' },
    { value: 'plants', label: 'Nibbles on houseplants', icon: '\uD83E\uDEB4' },
    { value: 'none', label: 'Nothing specific, just overall wellness', icon: '\u2728' }
  ];

  var PERSONALITY_OPTIONS = [
    { value: 'social_vocal', icon: '\uD83D\uDE38', label: 'Social & vocal', desc: 'Loves attention, always chatting' },
    { value: 'independent_quiet', icon: '\uD83D\uDE3C', label: 'Independent & quiet', desc: 'Does their own thing, low-key' },
    { value: 'shy_hidden', icon: '\uD83D\uDE40', label: 'Shy & hidden', desc: 'Takes time to warm up, hides from visitors' },
    { value: 'playful_wild', icon: '\uD83D\uDE39', label: 'Playful & wild', desc: 'Always running, jumping, causing chaos' }
  ];

  var DIET_OPTIONS = [
    { value: 'dry', label: 'Dry food (kibble)', icon: '\uD83E\uDD63' },
    { value: 'wet', label: 'Wet food', icon: '\uD83E\uDD6B' },
    { value: 'mixed', label: 'Mix of both', icon: '\uD83C\uDF7D\uFE0F' },
    { value: 'raw', label: 'Raw / homemade', icon: '\uD83E\uDD69' }
  ];

  var ANXIETY_OPTIONS = [
    { value: 'calm', label: 'Pretty chill', icon: '\uD83D\uDE0E', desc: 'Relaxed, confident, no worries' },
    { value: 'mild', label: 'A little nervous sometimes', icon: '\uD83D\uDE1F', desc: 'Startles at loud noises, takes time to warm up' },
    { value: 'moderate', label: 'Gets anxious often', icon: '\uD83D\uDE30', desc: 'Hides under furniture, avoids visitors' },
    { value: 'high', label: 'Very anxious or stressed', icon: '\uD83D\uDE31', desc: 'Constant hiding, overgrooming, changes in appetite' }
  ];

  // ─── COPY TEMPLATES (every product+goal combo) ────────────

  var COPY_TEMPLATES = {
    reasons: {
      barley_hairballs: 'All three grasses help with hairballs, and for {{name}} we picked barley: harvested young, with broad leaves and more in every bite. It naturally contains vitamins C and E and carotenoids.',
      barley_digestion: 'For {{name}} we picked barley: harvested young, when its leaves hold the most natural antioxidants, with broad leaves to nibble at their own pace. It naturally contains vitamins C and E and carotenoids.',
      barley_stress: 'Barley gives {{name}} broad leaves with more in every bite, something to nibble at their own pace. We harvest it young, when the leaves hold the most natural antioxidants.',
      barley_enrichment: 'Barley gives {{name}} broad leaves with more in every bite. We harvest it young, and it naturally contains vitamins C and E and carotenoids.',
      barley_loves: 'Since {{name}} already enjoys grass, we picked barley: broad leaves with more in every bite. It is harvested young and naturally contains vitamins C and E and carotenoids.',

      oat_hairballs: '{{name}} can be particular, so we went with oat: crisp grass. Like all three grasses, it helps with hairballs, and it naturally contains fiber and minerals.',
      oat_digestion: 'For {{name}} we picked oat: crisp grass for cats who are particular about what they eat. It naturally contains fiber and minerals. If {{name}} has a health condition, check with your vet before adding anything new.',
      oat_stress: 'For {{name}} we picked oat: crisp grass. It naturally contains fiber and minerals.',
      oat_enrichment: 'Oat is crisp grass, a good fit for {{name}}. It naturally contains fiber and minerals.',
      oat_picky: 'Since {{name}} is particular about what they eat, we picked oat: crisp grass. It naturally contains fiber and minerals.',
      oat_tried_disliked: '{{name}} has not taken to grass yet, so we picked oat: crisp grass. It naturally contains fiber and minerals.',
      oat_kitten_picky: 'For a young, particular eater like {{name}}, we picked oat: crisp grass. Offer a little at a time while you watch.',

      wheat_enrichment: 'Wheat makes a daily ritual for {{name}}: soft, fine grass to return to whenever they need it. It naturally contains chlorophyll, vitamins C and E, and trace minerals.',
      wheat_hairballs: 'All three grasses help with hairballs, and for {{name}} we picked wheat: soft, fine grass to return to every day. It naturally contains chlorophyll, vitamins C and E, and trace minerals.',
      wheat_kitten: 'For a young cat like {{name}}, we picked wheat: soft, fine grass to explore at their own pace. It naturally contains chlorophyll, vitamins C and E, and trace minerals.',
      wheat_default: 'Wheat makes a daily ritual for {{name}}: soft, fine grass to return to whenever they need it. It naturally contains chlorophyll, vitamins C and E, and trace minerals.',

      bundle_complete: 'With {{count}} cats in the family, the Complete Wellness Bundle gives everyone variety: wheat, oat and barley on rotation, so every cat can find the grass they enjoy.',
      bundle_digestion: 'Both grasses help with hairballs: the Digestion Duo alternates crisp oat and broad-leaf barley every week for {{names}}.',
      bundle_vitality: 'Both grasses help with hairballs: the Vitality Pair alternates soft, fine wheat and broad-leaf barley every week for {{names}}.',
      bundle_nourishment: 'Both grasses help with hairballs: the Nourishment Mix alternates soft, fine wheat and crisp oat every week, for {{names}} and any particular eaters among them.'
    },

    bullets: {
      barley: ['Harvested young, with broad leaves', 'More in every bite', 'Naturally contains vitamins C and E and carotenoids'],
      oat: ['Crisp grass', 'For cats who are particular eaters', 'Naturally contains fiber and minerals'],
      wheat: ['Soft, fine grass', 'A daily ritual for cats prone to stress', 'Naturally contains chlorophyll, vitamins C and E'],
      bundle_complete: ['All 3 grasses on rotation', 'Variety for every cat', 'Free delivery on every plan'],
      bundle_digestion: ['Crisp oat and broad-leaf barley', 'Both help with hairballs', 'Alternates every week'],
      bundle_vitality: ['Soft wheat and broad-leaf barley', 'Both help with hairballs', 'Alternates every week'],
      bundle_nourishment: ['Soft wheat and crisp oat', 'For particular eaters', 'Alternates every week']
    }
  };

  // ─── STATE ────────────────────────────────────────────────

  function createInitialQuizState() {
    return {
      step: STEPS.INTRO,
      catCount: 1,
      cats: [makeCat()],
      activeCat: 0,
      simplifiedMode: false,
      recommendation: null,
      frequency: 'biweekly',
      email: '',
      emailSubmitted: false,
      addedToCart: false,
      quizSessionId: '',
      quizCompletedAt: '',
      quizCompletedTracked: false,
      emailCapturedTracked: false,
      resultViewedTracked: false,
      firstName: '',
      metaAdMeasurementChecked: false,
      consentChallengeHandle: '',
      consentChallengeSessionId: '',
      consentChallengeExpiresAt: '',
      consentChallengePending: false
    };
  }

  var state = createInitialQuizState();

  var AUTO_ADVANCE_ACTIONS = {
    select_age: true,
    select_appetite: true,
    select_diet: true,
    select_coat: true,
    select_experience: true,
    select_anxiety: true,
    select_personality: true
  };

  function makeCat() {
    return {
      name: '',
      age: '',        // kitten | young_adult | mature | senior
      appetite: '',   // adventurous | cautious | picky
      diet: '',       // dry | wet | mixed | raw
      coat: '',       // short | long | hairless
      concerns: [],   // hairballs | digestion | boredom | plants | none
      experience: '', // first_time | tried_disliked | loves_it
      anxiety: '',    // calm | mild | moderate | high
      personality: '' // social_vocal | independent_quiet | shy_hidden | playful_wild
    };
  }

  // ─── DOM REFS & CONFIG ────────────────────────────────────

  var root, container, progressEl, progressBar, backBtn, navEl, nextBtn;
  var CONFIG = {};
  var PRODUCTS = {};
  var IMAGES = {};
  var PRODUCT_IMAGE_MAP = {};
  var PRICE_CACHE = {};
  var PRODUCT_JSON_CACHE = {};
  var autoAdvanceTimer = null;
  var consentChallengePromise = null;
  var quizStartedAt = Date.now();
  var lastTrackedStepViewKey = '';
  var lastTrackedFactKey = '';
  var QUIZ_SESSION_STORAGE_KEY = 'sprouties_quiz_session_id';
  var QUIZ_STATE_STORAGE_KEY = 'sprouties_quiz_state';
  var QUIZ_CART_SCHEMA_VERSION = '2026-05-02';
  var META_AD_POLICY_VERSION = 'rank5-v13';
  var META_AD_DISCLOSURE_ID = 'quiz_meta_ad_measurement_rank5_v13';
  var META_AD_DISCLOSURE_COPY = 'Also use my email to measure Sprouties ads (shared hashed with Meta)';

  // ─── INIT ─────────────────────────────────────────────────

  function init() {
    root = document.querySelector('[data-quiz-v2]');
    if (!root) return;

    container = root.querySelector('[data-q2-container]');
    progressEl = root.querySelector('[data-q2-progress]');
    progressBar = root.querySelector('[data-q2-progress-bar]');
    backBtn = root.querySelector('[data-q2-back]');
    navEl = root.querySelector('[data-q2-nav]');
    nextBtn = root.querySelector('[data-q2-next]');

    try {
      CONFIG = JSON.parse(root.querySelector('[data-q2-config]').textContent);
    } catch (e) {
      console.error('[Quiz V2] Config JSON parse failed:', e);
      CONFIG = {};
    }

    try {
      PRODUCTS = JSON.parse(root.querySelector('[data-q2-products]').textContent);
    } catch (e) {
      console.error('[Quiz V2] Products JSON parse failed:', e);
      PRODUCTS = {};
    }

    // Read image URLs from data attributes (bypasses JSON, same pipeline as working theme sections)
    IMAGES = {
      logo: root.dataset.imgLogo || '',
      intro: root.dataset.imgIntro || '',
      productComplete: root.dataset.imgProductComplete || '',
      productDigestion: root.dataset.imgProductDigestion || '',
      productVitality: root.dataset.imgProductVitality || '',
      productNourish: root.dataset.imgProductNourish || '',
      productBarley: root.dataset.imgProductBarley || '',
      productWheat: root.dataset.imgProductWheat || '',
      productOat: root.dataset.imgProductOat || ''
    };

    // Map recommendation product keys to IMAGES keys
    PRODUCT_IMAGE_MAP = {
      bundle_complete: 'productComplete',
      bundle_digestion: 'productDigestion',
      bundle_vitality: 'productVitality',
      bundle_nourishment: 'productNourish',
      barley: 'productBarley',
      wheat: 'productWheat',
      oat: 'productOat'
    };

    console.log('[Quiz V2] Config loaded:', Object.keys(CONFIG).length, 'keys. Review1:', CONFIG.review1Text ? 'yes' : 'no');
    console.log('[Quiz V2] Products loaded:', Object.keys(PRODUCTS).length, 'products');
    console.log('[Quiz V2] Images loaded from data attrs. Logo:', IMAGES.logo ? 'yes' : 'no', 'Intro:', IMAGES.intro ? 'yes' : 'no');

    // Event delegation
    container.addEventListener('click', handleClick);
    container.addEventListener('input', handleInput);
    if (nextBtn) nextBtn.addEventListener('click', handleNext);
    if (backBtn) backBtn.addEventListener('click', handleBack);

    container.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && nextBtn && !nextBtn.disabled && navEl.style.display !== 'none') {
        handleNext();
      }
    });

    if (CONFIG.fullscreen !== false) {
      document.body.classList.add('quiz-v2-active');
    }

    restoreState();
    fireEvent('quiz_viewed');
    render();
  }

  // ─── RENDER DISPATCHER ────────────────────────────────────

  function render() {
    updateProgress();
    updateBackButton();
    updateNav();

    var renderers = {
      intro: renderIntro,
      cat_names: renderCatNames,
      age: renderAge,
      appetite: renderAppetite,
      diet: renderDiet,
      coat: renderCoat,
      concerns: renderConcerns,
      experience: renderExperience,
      anxiety: renderAnxiety,
      personality: renderPersonality,
      analyzing: renderAnalyzing,
      results: renderResults
    };

    var fn = renderers[state.step];
    if (fn) fn();

    trackStepViewed();
    trackVisibleFact();

    saveState();
    container.scrollTop = 0;
    root.scrollTop = 0;
  }

  // ─── PROGRESS BAR ────────────────────────────────────────

  function getProgressPercent() {
    if (state.step === STEPS.INTRO) return 0;
    if (state.step === STEPS.RESULTS) return 100;

    var base = PROGRESS_MAP[state.step] || 0;

    // For multi-cat, interpolate within per-cat steps
    if (!state.simplifiedMode && state.catCount > 1 && PER_CAT_STEPS.indexOf(state.step) >= 0) {
      var catFraction = state.activeCat / state.catCount;
      var stepIdx = PER_CAT_STEPS.indexOf(state.step);
      var stepsPerCat = PER_CAT_STEPS.length;
      var totalPerCatRange = 70; // 8 to 78
      var perCatSlice = totalPerCatRange / state.catCount;
      base = 8 + (state.activeCat * perCatSlice) + ((stepIdx / stepsPerCat) * perCatSlice);
    }

    return Math.round(Math.min(base, 95));
  }

  function updateProgress() {
    var show = state.step !== STEPS.INTRO;
    progressEl.classList.toggle('quiz-v2__progress--visible', show);
    var pct = getProgressPercent();
    progressBar.style.width = pct + '%';
    progressEl.setAttribute('aria-valuenow', pct);
  }

  function updateBackButton() {
    var canGoBack = state.step !== STEPS.INTRO && state.step !== STEPS.RESULTS && state.step !== STEPS.ANALYZING;
    backBtn.classList.toggle('quiz-v2__back--visible', canGoBack);
  }

  function updateNav() {
    var showNav = state.step !== STEPS.INTRO && state.step !== STEPS.ANALYZING && state.step !== STEPS.RESULTS;
    navEl.style.display = showNav ? '' : 'none';

    if (showNav) {
      nextBtn.textContent = getNextButtonText();
      nextBtn.disabled = !canAdvance();
    }
  }

  function getNextButtonText() {
    var cat = state.cats[state.activeCat];
    var name = cat ? cat.name : 'your cat';
    if (!name) name = 'your cat';

    if (state.step === PER_CAT_STEPS[PER_CAT_STEPS.length - 1]) {
      // Last per-cat step
      if (!state.simplifiedMode && state.activeCat < state.catCount - 1) {
        var nextName = state.cats[state.activeCat + 1] ? state.cats[state.activeCat + 1].name : 'next cat';
        return 'Next: About ' + (nextName || 'next cat');
      }
      return 'See ' + name + "'s recommendation";
    }

    return 'Continue';
  }

  // ─── VALIDATION ───────────────────────────────────────────

  function canAdvance() {
    var cat = state.cats[state.activeCat];

    switch (state.step) {
      case STEPS.CAT_NAMES:
        for (var i = 0; i < state.catCount; i++) {
          if (!state.cats[i] || !state.cats[i].name.trim()) return false;
        }
        return state.catCount >= 1;

      case STEPS.AGE:
        return cat && cat.age !== '';
      case STEPS.APPETITE:
        return cat && cat.appetite !== '';
      case STEPS.DIET:
        return cat && cat.diet !== '';
      case STEPS.COAT:
        return cat && cat.coat !== '';
      case STEPS.CONCERNS:
        return cat && cat.concerns.length > 0;
      case STEPS.EXPERIENCE:
        return cat && cat.experience !== '';
      case STEPS.ANXIETY:
        return cat && cat.anxiety !== '';
      case STEPS.PERSONALITY:
        return cat && cat.personality !== '';
      default:
        return true;
    }
  }

  // ─── HELPERS: Multi-cat indicator + step image ────────────

  function catIndicator() {
    if (state.catCount <= 1 || state.simplifiedMode) return '';
    var cat = state.cats[state.activeCat];
    var name = cat ? cat.name : '';
    return '<div class="quiz-v2__cat-indicator">\uD83D\uDC31 ' + esc(name || 'Cat') + ' (' + (state.activeCat + 1) + ' of ' + state.catCount + ')</div>';
  }

  function logoHtml() {
    var src = IMAGES.logo || '';
    if (!src) return '';
    return '<div class="quiz-v2__logo"><img src="' + esc(src) + '" alt="Sprouties" /></div>';
  }

  function getProductImage(productKey) {
    var imgKey = PRODUCT_IMAGE_MAP[productKey] || '';
    return imgKey ? (IMAGES[imgKey] || '') : '';
  }

  // ─── STEP RENDERERS ──────────────────────────────────────

  function renderIntro() {
    var imgSrc = IMAGES.intro || '';
    var heading = CONFIG.introHeading || "Find your cat's perfect fresh routine";
    var sub = CONFIG.introSub || 'Answer a few quick questions.';
    var social = getSocialProofText();
    var cta = CONFIG.introCta || "Let's find the right grass";
    var b1 = CONFIG.trustBadge1 || '100% Organic';
    var b2 = CONFIG.trustBadge2 || 'Fresh, Never Dried';
    var b3 = CONFIG.trustBadge3 || 'Free Delivery';

    container.innerHTML =
      '<div class="quiz-v2__step quiz-v2__intro">' +
        logoHtml() +
        (imgSrc ? '<div class="quiz-v2__intro-image"><img src="' + esc(imgSrc) + '" alt="Cat with fresh grass" loading="eager" /></div>' : '') +
        '<h1 class="quiz-v2__intro-heading">' + esc(heading) + '</h1>' +
        '<p class="quiz-v2__intro-sub">' + esc(sub) + '</p>' +
        '<div class="quiz-v2__intro-social">' +
          '<span class="quiz-v2__intro-social-dot"></span>' +
          esc(social) +
        '</div>' +
        '<button class="quiz-v2__intro-cta" data-action="start">' + esc(cta) + '</button>' +
        '<div class="quiz-v2__trust-row">' +
          '<span class="quiz-v2__trust-badge">' + trustIcon() + ' ' + esc(b1) + '</span>' +
          '<span class="quiz-v2__trust-badge">' + trustIcon() + ' ' + esc(b2) + '</span>' +
          '<span class="quiz-v2__trust-badge">' + trustIcon() + ' ' + esc(b3) + '</span>' +
        '</div>' +
      '</div>';
  }

  function renderCatNames() {
    var nameInputs = '';
    var count = Math.min(state.catCount, 6);
    for (var i = 0; i < count; i++) {
      var val = state.cats[i] ? state.cats[i].name : '';
      var placeholder = count === 1 ? "Your cat's name" : 'Cat ' + (i + 1) + "'s name";
      nameInputs +=
        '<div class="quiz-v2__name-row">' +
          '<input class="quiz-v2__input" type="text" data-input="cat_name" data-index="' + i + '" placeholder="' + placeholder + '" value="' + esc(val) + '" autocomplete="off" />' +
          (count > 1 ? '<button class="quiz-v2__name-remove" data-action="remove_cat" data-index="' + i + '" aria-label="Remove">&times;</button>' : '') +
        '</div>';
    }

    container.innerHTML =
      '<div class="quiz-v2__step">' +
        '<h2 class="quiz-v2__question-heading">Who are we matching today?</h2>' +
        '<p class="quiz-v2__question-sub">We\'ll personalize recommendations for each cat</p>' +
        '<div class="quiz-v2__input-group">' +
          nameInputs +
        '</div>' +
        (count < 6 ? '<button class="quiz-v2__add-cat-btn" data-action="add_cat">+ Add another cat</button>' : '') +
      '</div>';

    // Focus first empty input
    var inputs = container.querySelectorAll('.quiz-v2__input');
    for (var j = 0; j < inputs.length; j++) {
      if (!inputs[j].value) {
        setTimeout(function () { inputs[j].focus(); }, 100);
        break;
      }
    }
  }

  function renderAge() {
    var cat = state.cats[state.activeCat];
    var name = cat.name || 'your cat';

    container.innerHTML =
      '<div class="quiz-v2__step">' +
        catIndicator() +
        '<h2 class="quiz-v2__question-heading">How old is ' + esc(name) + '?</h2>' +
        '<div class="quiz-v2__options-grid quiz-v2__options-grid--2">' +
          ageOption('kitten', '\uD83D\uDC31', 'Kitten', 'Under 1 year', cat.age) +
          ageOption('young_adult', '\uD83D\uDE3A', 'Young Adult', '1\u20137 years', cat.age) +
          ageOption('mature', '\uD83D\uDC08', 'Mature', '7\u201311 years', cat.age) +
          ageOption('senior', '\uD83D\uDC08\u200D\u2B1B', 'Senior', '11+ years', cat.age) +
        '</div>' +
        ageFact(cat) +
      '</div>';
  }

  function renderAppetite() {
    var cat = state.cats[state.activeCat];
    var name = cat.name || 'your cat';

    container.innerHTML =
      '<div class="quiz-v2__step">' +
        catIndicator() +
        '<h2 class="quiz-v2__question-heading">How does ' + esc(name) + ' handle new foods?</h2>' +
        '<div class="quiz-v2__options-grid quiz-v2__options-grid--3">' +
          appetiteOption('adventurous', '\uD83D\uDE3B', 'Eats anything', cat.appetite) +
          appetiteOption('cautious', '\uD83E\uDD14', 'Cautious', cat.appetite) +
          appetiteOption('picky', '\uD83D\uDE44', 'Very picky', cat.appetite) +
        '</div>' +
        (cat.appetite === 'picky' ? factBox(getFactPickyText()) : '') +
      '</div>';
  }

  function renderDiet() {
    var cat = state.cats[state.activeCat];
    var name = cat.name || 'your cat';

    container.innerHTML =
      '<div class="quiz-v2__step">' +
        catIndicator() +
        '<h2 class="quiz-v2__question-heading">What do you feed ' + esc(name) + '?</h2>' +
        '<div class="quiz-v2__options-grid quiz-v2__options-grid--2">' +
          DIET_OPTIONS.map(function (o) {
            return dietOption(o.value, o.icon, o.label, cat.diet);
          }).join('') +
        '</div>' +
      '</div>';
  }

  function renderCoat() {
    var cat = state.cats[state.activeCat];
    var name = cat.name || 'your cat';

    container.innerHTML =
      '<div class="quiz-v2__step">' +
        catIndicator() +
        '<h2 class="quiz-v2__question-heading">What\'s ' + esc(name) + '\'s coat like?</h2>' +
        '<div class="quiz-v2__options-grid quiz-v2__options-grid--3">' +
          coatOption('short', '\uD83D\uDC31', 'Short-haired', cat.coat) +
          coatOption('long', '\uD83E\uDD81', 'Long-haired', cat.coat) +
          coatOption('hairless', '\u2728', 'Hairless', cat.coat) +
        '</div>' +
        (cat.coat === 'long' ? factBox(CONFIG.factCoat || 'Long-haired cats swallow more fur when they groom. Fresh grass helps with hairballs.') : '') +
      '</div>';
  }

  function renderConcerns() {
    var cat = state.cats[state.activeCat];
    var name = cat.name || 'your cat';

    var heading = state.simplifiedMode
      ? 'What should we plan around for this group?'
      : 'Does ' + esc(name) + ' deal with any of these?';
    var sub = state.simplifiedMode
      ? 'Select anything that applies across your cats'
      : 'Select all that apply';

    container.innerHTML =
      '<div class="quiz-v2__step">' +
        catIndicator() +
        '<h2 class="quiz-v2__question-heading">' + heading + '</h2>' +
        '<p class="quiz-v2__question-sub">' + sub + '</p>' +
        '<div class="quiz-v2__checkbox-group">' +
          CONCERNS_OPTIONS.map(function (o) { return concernCheckbox(o, cat.concerns); }).join('') +
        '</div>' +
        concernsFact(cat) +
      '</div>';
  }

  function renderExperience() {
    var cat = state.cats[state.activeCat];
    var name = cat.name || 'your cat';

    container.innerHTML =
      '<div class="quiz-v2__step">' +
        catIndicator() +
        '<h2 class="quiz-v2__question-heading">Has ' + esc(name) + ' tried fresh cat grass before?</h2>' +
        '<div class="quiz-v2__options-grid quiz-v2__options-grid--3">' +
          experienceOption('first_time', '\uD83C\uDD95', 'First time', cat.experience) +
          experienceOption('tried_disliked', '\uD83D\uDE12', "Tried, wasn't into it", cat.experience) +
          experienceOption('loves_it', '\uD83D\uDE0D', 'Loves it', cat.experience) +
        '</div>' +
        (cat.experience === 'first_time' ? factBox(CONFIG.factFirstTimer || 'Most cats nibble grass, and most are healthy when they do.') : '') +
      '</div>';
  }

  function renderPersonality() {
    var cat = state.cats[state.activeCat];
    var name = cat.name || 'your cat';

    container.innerHTML =
      '<div class="quiz-v2__step">' +
        catIndicator() +
        '<h2 class="quiz-v2__question-heading">Which best describes ' + esc(name) + '?</h2>' +
        '<p class="quiz-v2__question-sub">This helps us match texture and experience to their temperament</p>' +
        '<div class="quiz-v2__options-list">' +
          PERSONALITY_OPTIONS.map(function (o) {
            return listOption(o.value, o.icon, o.label, o.desc, cat.personality, 'select_personality');
          }).join('') +
        '</div>' +
      '</div>';
  }

  function renderAnxiety() {
    var cat = state.cats[state.activeCat];
    var name = cat.name || 'your cat';

    container.innerHTML =
      '<div class="quiz-v2__step">' +
        catIndicator() +
        '<h2 class="quiz-v2__question-heading">How would you describe ' + esc(name) + '\'s anxiety level?</h2>' +
        '<p class="quiz-v2__question-sub">This helps us pick the right variety for their temperament</p>' +
        '<div class="quiz-v2__options-list">' +
          ANXIETY_OPTIONS.map(function (o) {
            return listOption(o.value, o.icon, o.label, o.desc, cat.anxiety, 'select_anxiety');
          }).join('') +
        '</div>' +
      '</div>';
  }

  function renderAnalyzing() {
    var names = getCatNamesString();
    var duration = CONFIG.analyzingDuration || 3500;
    var maxFacts = CONFIG.maxFacts || 3;
    var facts = getAnalyzingFacts(maxFacts);

    container.innerHTML =
      '<div class="quiz-v2__step quiz-v2__analyzing">' +
        logoHtml() +
        '<div class="quiz-v2__analyzing-spinner"></div>' +
        '<h2 class="quiz-v2__analyzing-heading">Finding the perfect match for ' + esc(names) + '...</h2>' +
        '<div class="quiz-v2__analyzing-fact" data-q2-analyzing-fact></div>' +
      '</div>';

    var factEl = container.querySelector('[data-q2-analyzing-fact]');
    var factIndex = 0;

    if (facts.length > 0 && factEl) {
      factEl.textContent = facts[0];
      var interval = Math.floor(duration / Math.min(facts.length, maxFacts));

      var factTimer = setInterval(function () {
        factIndex++;
        if (factIndex >= facts.length) {
          clearInterval(factTimer);
          return;
        }
        factEl.style.opacity = '0';
        setTimeout(function () {
          factEl.textContent = facts[factIndex];
          factEl.style.opacity = '1';
        }, 200);
      }, interval);
    }

    setTimeout(function () {
      progressBar.style.width = '100%';
    }, 100);

    setTimeout(function () {
      applyRecommendation(calculateRecommendation());
      state.step = STEPS.RESULTS;
      render();
      var rec = state.recommendation;
      var eventProduct = rec.isMultiProduct
        ? rec.items.map(function (i) { return i.product; }).join('+')
        : rec.product;
      fireEvent('quiz_recommendation_shown', {
        product: eventProduct,
        reason: rec.ruleId,
        cat_count: state.catCount
      });
    }, duration);
  }

  // ─── DATA CAPTURE: Cart Attributes + Session ID ─────────

  function createQuizSessionId() {
    var cryptoObj = window.crypto || window.msCrypto;
    return cryptoObj && typeof cryptoObj.randomUUID === 'function'
      ? cryptoObj.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          var r = Math.random() * 16 | 0;
          return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
  }

  function getQuizSessionId() {
    if (state.quizSessionId) return state.quizSessionId;
    var savedSessionId = '';
    try {
      savedSessionId = window.sessionStorage ? window.sessionStorage.getItem(QUIZ_SESSION_STORAGE_KEY) : '';
    } catch (e) {}

    state.quizSessionId = savedSessionId || createQuizSessionId();

    if (!savedSessionId) {
      try {
        if (window.sessionStorage) {
          window.sessionStorage.setItem(QUIZ_SESSION_STORAGE_KEY, state.quizSessionId);
        }
      } catch (e) {}
    }

    return state.quizSessionId;
  }

  function generateSessionIfNeeded() {
    getQuizSessionId();
    if (!state.quizCompletedAt) {
      state.quizCompletedAt = new Date().toISOString();
    }
  }

  function getProductString(rec) {
    if (!rec) return '';
    return rec.isMultiProduct
      ? rec.items.map(function(i) { return i.product; }).join('+')
      : rec.product;
  }

  function getProductFrequencyString(rec) {
    if (!rec) return state.frequency;
    if (rec.isMultiProduct && rec.items && rec.items.length) {
      return rec.items.map(function(item) {
        return item.product + ':' + (item.recommendedFrequency || state.frequency || '');
      }).join('+');
    }
    return state.frequency;
  }

  function getQuizVersion() {
    return CONFIG.quizVersion || 'v2';
  }

  function joinCartAttributeValues(values) {
    return (values || []).map(function(value) {
      return value == null ? '' : String(value);
    }).filter(function(value) {
      return value !== '';
    }).join(',');
  }

  function getRecommendationId(rec) {
    return rec && rec.ruleId ? rec.ruleId : '';
  }

  function getRecommendationCartFields(rec, isTrial) {
    var entries = getRecommendationEntries(rec);
    var cartItems = getCartItems(rec, !!isTrial);
    var handles = [];
    var variantIds = [];
    var sellingPlanIds = [];

    entries.forEach(function(entry, index) {
      var product = PRODUCTS[entry.product] || {};
      var cartItem = cartItems[index] || cartItems[0] || {};
      handles.push(product.handle || entry.product);
      if (cartItem.id != null) variantIds.push(String(cartItem.id));
      if (cartItem.selling_plan != null) sellingPlanIds.push(String(cartItem.selling_plan));
    });

    return {
      recommendationType: rec && rec.isMultiProduct ? 'multi_product' : 'single',
      recommendedHandles: joinCartAttributeValues(handles),
      recommendedVariantIds: joinCartAttributeValues(variantIds),
      recommendedSellingPlanIds: joinCartAttributeValues(sellingPlanIds)
    };
  }

  function getLegacySensitiveAttributeClearances() {
    return {
      quiz_cat_names: '',
      quiz_cat_data: '',
      quiz_data: '',
      quiz_email: '',
      quiz_first_name: ''
    };
  }

  function buildCartAttributeSchema(rec, isTrial) {
    generateSessionIfNeeded();
    var fields = getRecommendationCartFields(rec, isTrial);
    var attrs = {
      quiz_session_id: state.quizSessionId,
      quiz_source: getAnalyticsExperience(),
      quiz_version: getQuizVersion(),
      quiz_completed: 'true',
      quiz_recommendation_type: fields.recommendationType,
      quiz_recommended_handles: fields.recommendedHandles,
      quiz_recommended_variant_ids: fields.recommendedVariantIds,
      quiz_recommended_selling_plan_ids: fields.recommendedSellingPlanIds,
      quiz_subscription_frequency: state.frequency,
      quiz_cat_count_bucket: getCatCountBucket(),
      quiz_email_captured: state.emailCapturedTracked ? 'true' : 'false',
      _quiz_recommendation_id: getRecommendationId(rec),
      _quiz_cat_count: String(state.catCount || 1),
      _quiz_schema_version: QUIZ_CART_SCHEMA_VERSION
    };

    // Temporary non-PII legacy bridge for the current orders-paid webhook.
    // Remove after 2026-06-15 once the webhook reads the public schema.
    attrs.quiz_completed_at = state.quizCompletedAt;
    attrs.quiz_product = getProductString(rec);
    attrs.quiz_product_frequencies = getProductFrequencyString(rec);
    attrs.quiz_frequency = state.frequency;
    attrs.quiz_rule = getRecommendationId(rec);
    attrs.quiz_is_trial = String(!!isTrial);

    var clearances = getLegacySensitiveAttributeClearances();
    Object.keys(clearances).forEach(function(key) {
      attrs[key] = clearances[key];
    });

    return attrs;
  }

  function writeQuizDataToCart() {
    var rec = state.recommendation;
    var attrs = buildCartAttributes(rec, false);

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function(e) { console.warn('Cart attribute write failed:', e); });
  }

  function fireQuizCompletedEvent() {
    if (state.quizCompletedTracked) return;

    var rec = state.recommendation;
    var productStr = getProductString(rec);
    var analytics = getRecommendationAnalytics(rec, false);
    var _learnq = window._learnq || [];

    _learnq.push(['track', 'Quiz Completed', {
      quiz_session_id: state.quizSessionId,
      quiz_version: getQuizVersion(),
      quiz_product: productStr,
      quiz_product_frequencies: getProductFrequencyString(rec),
      quiz_cat_count: state.catCount,
      quiz_cat_names: getCatNamesString(),
      quiz_frequency: state.frequency,
      quiz_concerns: state.cats.reduce(function(a, c) { return a.concat(c.concerns); }, []).join(','),
      quiz_completed_at: state.quizCompletedAt
    }]);
    analytics.quiz_session_id = state.quizSessionId;
    analytics.value = getCartValue(rec, false);
    analytics.currency = 'AED';
    fireEvent('quiz_completed', analytics);
    state.quizCompletedTracked = true;
  }

  function onResultsRendered() {
    generateSessionIfNeeded();
    if (CONFIG.enableEmail !== false && !state.emailSubmitted) {
      ensureConsentChallenge();
    }
    trackResultViewed();
    writeQuizDataToCart();
    fireQuizCompletedEvent();
  }

  // ─── DATA CAPTURE: Lead Endpoint + Klaviyo ─────────────

  function getShopifyRegionAudit() {
    try {
      var customerPrivacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!customerPrivacy || typeof customerPrivacy.getRegion !== 'function') return '';
      var region = customerPrivacy.getRegion();
      return typeof region === 'string' ? region.trim() : '';
    } catch (error) {
      return '';
    }
  }

  function ensureConsentChallenge() {
    if (!CONFIG.quizApiUrl) return Promise.resolve('');

    var quizSessionId = getQuizSessionId();
    if (state.consentChallengeSessionId !== quizSessionId) {
      state.consentChallengeHandle = '';
      state.consentChallengeSessionId = '';
      state.consentChallengeExpiresAt = '';
      state.consentChallengePending = false;
      consentChallengePromise = null;
    }

    if (state.consentChallengeHandle) {
      var cachedExpiry = Date.parse(state.consentChallengeExpiresAt);
      if (!isNaN(cachedExpiry) && cachedExpiry > Date.now()) {
        return Promise.resolve(state.consentChallengeHandle);
      }
      state.consentChallengeHandle = '';
      state.consentChallengeSessionId = '';
      state.consentChallengeExpiresAt = '';
      state.consentChallengePending = false;
      consentChallengePromise = null;
    }
    if (state.consentChallengePending && consentChallengePromise) {
      return consentChallengePromise;
    }

    state.consentChallengePending = true;
    consentChallengePromise = fetch(CONFIG.quizApiUrl + '/api/quiz/v1/consent-challenge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        quizSessionId: quizSessionId,
        regionAudit: getShopifyRegionAudit()
      })
    }).then(function(response) {
      if (!response.ok) throw new Error('Consent challenge request failed: ' + response.status);
      return response.json();
    }).then(function(data) {
      if (state.quizSessionId !== quizSessionId) return '';
      if (!data || data.success !== true || typeof data.challengeHandle !== 'string' || !data.challengeHandle) {
        throw new Error('Consent challenge response malformed');
      }
      if (data.policyVersion !== META_AD_POLICY_VERSION || data.disclosureId !== META_AD_DISCLOSURE_ID) {
        throw new Error('Consent challenge contract mismatch');
      }
      if (typeof data.grantable !== 'boolean' || typeof data.expiresAt !== 'string' || !data.expiresAt) {
        throw new Error('Consent challenge state malformed');
      }
      if (isNaN(Date.parse(data.expiresAt)) || Date.parse(data.expiresAt) <= Date.now()) {
        throw new Error('Consent challenge expiry invalid');
      }
      state.consentChallengeHandle = data.challengeHandle;
      state.consentChallengeSessionId = quizSessionId;
      state.consentChallengeExpiresAt = data.expiresAt;
      state.consentChallengePending = false;
      consentChallengePromise = null;
      return data.challengeHandle;
    }).catch(function(error) {
      if (state.quizSessionId === quizSessionId) {
        state.consentChallengeHandle = '';
        state.consentChallengeSessionId = '';
        state.consentChallengeExpiresAt = '';
        state.consentChallengePending = false;
      }
      consentChallengePromise = null;
      if (window.console && typeof window.console.warn === 'function') {
        window.console.warn('[Quiz V2] Consent challenge unavailable:', error);
      }
      return '';
    });

    return consentChallengePromise;
  }

  function buildConsentActionCandidate() {
    if (!state.metaAdMeasurementChecked) return null;
    if (!state.consentChallengeHandle) return null;
    if (state.consentChallengeSessionId !== state.quizSessionId) return null;
    if (!state.consentChallengeExpiresAt || isNaN(Date.parse(state.consentChallengeExpiresAt))) return null;
    if (Date.parse(state.consentChallengeExpiresAt) <= Date.now()) return null;

    return {
      metaAdMeasurement: {
        checked: true,
        policyVersion: META_AD_POLICY_VERSION,
        disclosureId: META_AD_DISCLOSURE_ID,
        challengeHandle: state.consentChallengeHandle
      }
    };
  }

  function buildLeadPayload() {
    var payload = {
      email: state.email,
      firstName: state.firstName || '',
      quizSessionId: state.quizSessionId,
      quizData: {
        version: getQuizVersion(),
        catCount: state.catCount,
        catData: state.cats.map(function(c) {
          return {
            name: c.name, age: c.age, appetite: c.appetite, diet: c.diet,
            coat: c.coat, concerns: c.concerns, experience: c.experience,
            anxiety: c.anxiety, personality: c.personality
          };
        }),
        product: getProductString(state.recommendation),
        productFrequencies: getProductFrequencyString(state.recommendation),
        frequency: state.frequency,
        ruleId: state.recommendation ? state.recommendation.ruleId : '',
        timestamp: state.quizCompletedAt
      }
    };
    var consentAction = buildConsentActionCandidate();
    if (consentAction) payload.consentAction = consentAction;
    return payload;
  }

  function sendLeadToBackend() {
    if (!state.email || !CONFIG.quizApiUrl) return;
    var payload = buildLeadPayload();

    fetch(CONFIG.quizApiUrl + '/api/quiz/v1/lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify(payload)
    }).catch(function(e) { console.warn('Lead endpoint failed:', e); });
  }

  function sendLeadAfterConsentResolution() {
    if (!state.metaAdMeasurementChecked) {
      sendLeadToBackend();
      return;
    }
    ensureConsentChallenge().then(function() {
      sendLeadToBackend();
    });
  }

  // ─── RESULTS RENDERING ─────────────────────────────────

  function renderResults() {
    var rec = state.recommendation;
    if (!rec) return;

    // Route to multi-product renderer for 3+ cats
    if (rec.isMultiProduct) {
      renderMultiProductResults();
      return;
    }

    var product = PRODUCTS[rec.product] || {};
    var displayTitle = getDisplayProductTitle(rec.product, product.title || rec.product);
    var names = getCatNamesString();
    var isMultiCat = state.catCount > 1;
    var firstName = state.cats[0] ? state.cats[0].name : 'your cat';

    var headingName = isMultiCat ? names : (firstName || 'your cat');
    var whyHeading = isMultiCat ? ('Why this works for ' + names) : ('Why this works for ' + (firstName || 'your cat'));
    var ctaText = isMultiCat
      ? 'Start their subscription now'
      : 'Start ' + (firstName || 'your cat') + "'s subscription now";

    var reasonKey = rec.copyKey;
    var reasonText = COPY_TEMPLATES.reasons[reasonKey] || COPY_TEMPLATES.reasons[rec.product + '_enrichment'] || '';
    reasonText = reasonText.replace(/\{\{name\}\}/g, firstName || 'your cat')
      .replace(/\{\{names\}\}/g, names)
      .replace(/\{\{count\}\}/g, String(state.catCount));

    var bullets = COPY_TEMPLATES.bullets[rec.productBase] || COPY_TEMPLATES.bullets[rec.product] || [];
    var subtitle = isMultiCat
      ? buildMultiCatSubtitle()
      : buildResultSubtitle(state.cats[0], firstName || 'your cat');
    var pills = getProductPills(rec);
    var perCatHtml = buildPerCatProfiles();

    var review1 = CONFIG.review1Text ? reviewHtml(CONFIG.review1Text, CONFIG.review1Name, CONFIG.review1Stars) : '';
    var review2 = CONFIG.review2Text ? reviewHtml(CONFIG.review2Text, CONFIG.review2Name, CONFIG.review2Stars) : '';
    var emailSection = CONFIG.enableEmail !== false && !state.emailSubmitted ? emailCaptureHtml(firstName || 'your cat') : '';

    var guarantee = CONFIG.guaranteeTitle ?
      '<div class="quiz-v2__guarantee">' +
        '<div class="quiz-v2__guarantee-title">' + esc(CONFIG.guaranteeTitle) + '</div>' +
        '<div class="quiz-v2__guarantee-text">' + esc(CONFIG.guaranteeText || '') + '</div>' +
      '</div>' : '';

    var trustBadges = buildTrustBadges();

    // Frequency toggle: show for single grasses, not for CWB (10-day only) or bundles
    var isSingleGrass = ['barley', 'oat', 'wheat'].indexOf(rec.product) >= 0;
    var freqToggle = '';
    if (isSingleGrass) {
      var biweeklyCents = getDisplayedPrice(rec.product, 'biweekly');
      var weeklyCents = getDisplayedPrice(rec.product, 'weekly');
      var biweeklyPrice = biweeklyCents != null ? formatPrice(biweeklyCents) : 'Price at checkout';
      var weeklyPrice = weeklyCents != null ? formatPrice(weeklyCents) : 'Price at checkout';
      freqToggle =
        '<div class="quiz-v2__frequency">' +
          '<button class="quiz-v2__freq-btn' + (state.frequency === 'biweekly' ? ' quiz-v2__freq-btn--active' : '') + '" data-action="set_frequency" data-value="biweekly">' +
            'Biweekly<span class="quiz-v2__freq-price">' + esc(biweeklyPrice) + '</span></button>' +
          '<button class="quiz-v2__freq-btn' + (state.frequency === 'weekly' ? ' quiz-v2__freq-btn--active' : '') + '" data-action="set_frequency" data-value="weekly">' +
            'Weekly<span class="quiz-v2__freq-price">' + esc(weeklyPrice) + '</span></button>' +
        '</div>';
    }

    var trialHtml =
      '<div class="quiz-v2__retake">' +
        '<button class="quiz-v2__retake-link" data-action="add_trial">Prefer a one-time trial box instead</button>' +
      '</div>';

    container.innerHTML =
      '<div class="quiz-v2__step quiz-v2__results">' +
        logoHtml() +
        '<div class="quiz-v2__results-header">' +
          '<h2 class="quiz-v2__results-heading">Here\'s the perfect match for ' + esc(headingName) + '.</h2>' +
          '<p class="quiz-v2__results-sub">' + esc(subtitle) + '</p>' +
        '</div>' +
        '<div class="quiz-v2__product-card">' +
          '<div class="quiz-v2__product-badge">Recommended</div>' +
          (getProductImage(rec.product) ? '<div class="quiz-v2__product-image"><img src="' + esc(getProductImage(rec.product)) + '" alt="' + esc(displayTitle || '') + '" loading="lazy" /></div>' : '') +
          '<div class="quiz-v2__product-body">' +
            '<div class="quiz-v2__product-name">' + esc(displayTitle) + '</div>' +
            pills +
            '<div class="quiz-v2__why-box">' +
              '<div class="quiz-v2__why-heading">' + esc(whyHeading) + '</div>' +
              '<div class="quiz-v2__why-text">' + esc(reasonText) + '</div>' +
            '</div>' +
            perCatHtml +
            '<ul class="quiz-v2__product-benefits">' +
              bullets.map(function (b) { return '<li class="quiz-v2__product-benefit">' + checkIcon() + ' ' + esc(b) + '</li>'; }).join('') +
            '</ul>' +
          '</div>' +
        '</div>' +
        freqToggle +
        '<div class="quiz-v2__delivery-note">Free next-day delivery across the UAE</div>' +
        trustBadges +
        guarantee +
        '<button class="quiz-v2__add-btn" data-action="add_to_cart">' +
          esc(ctaText) +
        '</button>' +
        trialHtml +
        ((review1 || review2) ?
          '<div class="quiz-v2__reviews">' +
            '<div class="quiz-v2__reviews-title">' + esc(CONFIG.reviewsTitle || 'What cat parents say') + '</div>' +
            review1 + review2 +
          '</div>' : '') +
        emailSection +
        '<div class="quiz-v2__retake"><button class="quiz-v2__retake-link" data-action="retake">Retake quiz</button></div>' +
      '</div>';

    onResultsRendered();
  }

  // ─── MULTI-PRODUCT RESULTS (3+ cats) ─────────────────────
  function renderMultiProductResults() {
    var rec = state.recommendation;
    var items = rec.items;
    var names = getCatNamesString();
    var perCatHtml = buildPerCatProfiles();

    var review1 = CONFIG.review1Text ? reviewHtml(CONFIG.review1Text, CONFIG.review1Name, CONFIG.review1Stars) : '';
    var review2 = CONFIG.review2Text ? reviewHtml(CONFIG.review2Text, CONFIG.review2Name, CONFIG.review2Stars) : '';
    var emailSection = CONFIG.enableEmail !== false && !state.emailSubmitted ? emailCaptureHtml(names) : '';

    var guarantee = CONFIG.guaranteeTitle ?
      '<div class="quiz-v2__guarantee">' +
        '<div class="quiz-v2__guarantee-title">' + esc(CONFIG.guaranteeTitle) + '</div>' +
        '<div class="quiz-v2__guarantee-text">' + esc(CONFIG.guaranteeText || '') + '</div>' +
      '</div>' : '';

    var trustBadges = buildTrustBadges();

    // Build a card for each recommended product
    var cardsHtml = '';
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var product = PRODUCTS[item.product] || {};
      var displayTitle = getDisplayProductTitle(item.product, product.title || item.product);
      var reasonText = COPY_TEMPLATES.reasons[item.copyKey] || COPY_TEMPLATES.reasons[item.product + '_enrichment'] || '';
      reasonText = reasonText.replace(/\{\{name\}\}/g, 'Your cats')
        .replace(/\{\{names\}\}/g, names)
        .replace(/\{\{count\}\}/g, String(state.catCount));
      reasonText = fixPluralReasonText(reasonText);
      var bullets = COPY_TEMPLATES.bullets[item.productBase] || COPY_TEMPLATES.bullets[item.product] || [];
      var pills = getProductPills(item);
      var freqLabel = item.recommendedFrequency === 'weekly' ? 'Weekly' : 'Biweekly';

      cardsHtml +=
        '<div class="quiz-v2__product-card quiz-v2__product-card--multi">' +
          '<div class="quiz-v2__product-badge">' + (i === 0 ? 'Primary' : 'Complementary') + '</div>' +
          (getProductImage(item.product) ? '<div class="quiz-v2__product-image"><img src="' + esc(getProductImage(item.product)) + '" alt="' + esc(displayTitle || '') + '" loading="lazy" /></div>' : '') +
          '<div class="quiz-v2__product-body">' +
            '<div class="quiz-v2__product-name">' + esc(displayTitle) + '</div>' +
            '<div class="quiz-v2__product-freq-label">' + esc(freqLabel) + ' delivery</div>' +
            pills +
            '<div class="quiz-v2__why-box">' +
              '<div class="quiz-v2__why-text">' + esc(reasonText) + '</div>' +
            '</div>' +
            '<ul class="quiz-v2__product-benefits">' +
              bullets.map(function (b) { return '<li class="quiz-v2__product-benefit">' + checkIcon() + ' ' + esc(b) + '</li>'; }).join('') +
            '</ul>' +
          '</div>' +
        '</div>';
    }

    container.innerHTML =
      '<div class="quiz-v2__step quiz-v2__results quiz-v2__results--multi">' +
        logoHtml() +
        '<div class="quiz-v2__results-header">' +
          '<h2 class="quiz-v2__results-heading">Here\'s the perfect plan for ' + esc(names) + '.</h2>' +
          '<p class="quiz-v2__results-sub">We recommend two products to cover all ' + state.catCount + ' cats across the family.</p>' +
        '</div>' +
        '<div class="quiz-v2__multi-cards">' +
          cardsHtml +
        '</div>' +
        perCatHtml +
        '<div class="quiz-v2__delivery-note">Free next-day delivery across the UAE</div>' +
        trustBadges +
        guarantee +
        '<button class="quiz-v2__add-btn" data-action="add_to_cart">' +
          'Start their subscriptions now' +
        '</button>' +
        ((review1 || review2) ?
          '<div class="quiz-v2__reviews">' +
            '<div class="quiz-v2__reviews-title">' + esc(CONFIG.reviewsTitle || 'What cat parents say') + '</div>' +
            review1 + review2 +
          '</div>' : '') +
        emailSection +
        '<div class="quiz-v2__retake"><button class="quiz-v2__retake-link" data-action="retake">Retake quiz</button></div>' +
      '</div>';

    onResultsRendered();
  }

  function buildTrustBadges() {
    return '<div class="quiz-v2__results-trust">' +
      '<div class="quiz-v2__results-trust-badge">' + trustIcon() + ' 100% Organic</div>' +
      '<div class="quiz-v2__results-trust-badge">' + trustIcon() + ' Pesticide-Free</div>' +
      '<div class="quiz-v2__results-trust-badge">' + trustIcon() + ' Cancel Anytime</div>' +
      '<div class="quiz-v2__results-trust-badge">' + trustIcon() + ' Happy Cats Guarantee</div>' +
    '</div>';
  }

  // ─── RECOMMENDATION ENGINE V4 ───────────────────────────────
  // Rules:
  //   - Complete Wellness Bundle: 1-cat only, 10-day rotation (no weekly/biweekly)
  //   - 2-cat: weekly bundles, default to Vitality (barley+wheat)
  //   - 3+ cats: TWO different products recommended together
  //   - Single-cat: weekly default, oat only when absolutely necessary
  //   - Oat = last resort (highest complaint rate). Use only for:
  //     tried_disliked, digestion, hairball+truly picky, anxiety+shy+picky
  // ──────────────────────────────────────────────────────────────

  function applyRecommendation(rec) {
    state.recommendation = rec;
    // For multi-product recs (3+ cats), frequency is per-item
    if (rec && rec.items && rec.items.length) {
      state.frequency = rec.items[0].recommendedFrequency || state.frequency;
    } else if (rec && rec.recommendedFrequency) {
      state.frequency = rec.recommendedFrequency;
    }
  }

  function calculateRecommendation() {
    var d = computeFields();

    // The grass for each cat (founder ruling 2026-09-29, age first): a mature or senior cat
    // gets barley whatever else was answered; otherwise a particular eater (or a cat that
    // tried grass and was not into it) gets oat; otherwise a cat prone to stress gets wheat.
    // Two- and three-cat picks include every one of these grasses.
    state.cats.slice(0, state.catCount).forEach(function (c) {
      if (c.age === 'mature' || c.age === 'senior') d.needBarley = true;
      else if (c.appetite === 'picky' || c.experience === 'tried_disliked') d.needOat = true;
      else if (c.anxiety === 'moderate' || c.anxiety === 'high') d.needWheat = true;
    });

    // ── 3+ CATS: Two different products ──────────────────────
    if (d.catCount >= 3) {
      return calculateMultiProductRec(d);
    }

    // ── 2 CATS: Weekly bundles ───────────────────────────────
    if (d.catCount === 2) {
      if (d.needOat && d.needWheat) return makeRec('bundle_nourishment', 'bundle_nourishment', 'rule_2cat_anxiety_acceptance', 'weekly');
      if (d.needOat && d.needBarley) return makeRec('bundle_digestion', 'bundle_digestion', 'rule_2cat_picky_senior', 'weekly');
      if (d.needOat) return makeRec('bundle_nourishment', 'bundle_nourishment', d.hasHairballs ? 'rule_2cat_hairball_picky' : 'rule_2cat_picky', 'weekly');
      if (d.needWheat) return makeRec('bundle_vitality', 'bundle_vitality', d.hasHairballs ? 'rule_2cat_hairball' : 'rule_2cat_default', 'weekly');
      if (d.hasDigestion) return makeRec('bundle_digestion', 'bundle_digestion', 'rule_2cat_digestion', 'weekly');
      if (d.hasHairballs) return makeRec('bundle_vitality', 'bundle_vitality', 'rule_2cat_hairball', 'weekly');
      // Default 2-cat: Vitality (barley + wheat), weekly
      return makeRec('bundle_vitality', 'bundle_vitality', 'rule_2cat_default', 'weekly');
    }

    // ── 1 CAT ────────────────────────────────────────────────
    // Canon routing (founder ruling 2026-09-29, age first): mature and senior cats to barley
    // whatever else was answered; then particular eaters (and cats that tried grass and were
    // not into it) to oat; then cats prone to stress to wheat. Young adults and kittens have
    // no age rule: they fall through to their other answers (default wheat).

    if (d.hasSenior) {
      if (d.hasHairballs && d.hasLongHair) return makeRec('barley', 'barley_hairballs', 'rule_hairball_longcoat', 'weekly');
      if (d.hasHairballs) return makeRec('barley', 'barley_hairballs', 'rule_hairball_senior', 'weekly');
      return makeRec('barley', 'barley_enrichment', 'rule_senior_default', 'weekly');
    }

    if (d.anyTriedDisliked) {
      return makeRec('oat', 'oat_tried_disliked', 'rule_tried_disliked', 'weekly');
    }

    if (d.isPicky) {
      if (d.hasHairballs) return makeRec('oat', 'oat_hairballs', 'rule_hairball_picky', 'weekly');
      if (d.hasHighAnxiety) return makeRec('oat', 'oat_stress', 'rule_anxiety_shy_picky', 'weekly');
      return makeRec('oat', d.hasKitten ? 'oat_kitten_picky' : 'oat_picky', 'rule_picky', 'weekly');
    }

    if (d.hasHighAnxiety) {
      return makeRec('wheat', d.hasHairballs ? 'wheat_hairballs' : 'wheat_enrichment', 'rule_anxiety_foraging', 'weekly');
    }

    if (d.hasDigestion) {
      return makeRec('oat', 'oat_digestion', 'rule_digestion', 'weekly');
    }

    if (d.hasHairballs && d.hasLongHair) {
      return makeRec('barley', 'barley_hairballs', 'rule_hairball_longcoat', 'weekly');
    }

    if (d.hasHairballs) {
      return makeRec('barley', 'barley_hairballs', 'rule_hairball', 'weekly');
    }

    if (d.hasBoredom) {
      return makeRec('barley', 'barley_enrichment', 'rule_boredom_barley', 'weekly');
    }

    if (d.allLoveGrass) {
      return makeRec('barley', 'barley_loves', 'rule_loves_grass', 'weekly');
    }

    if (d.hasLongHair) {
      return makeRec('wheat', 'wheat_hairballs', 'rule_long_coat_default', 'weekly');
    }

    return makeRec('wheat', 'wheat_default', 'rule_default', 'weekly');
  }

  // ── 3+ CATS: Recommend two complementary products ─────────
  function calculateMultiProductRec(d) {
    var itemA, itemB;

    if (d.needOat) {
      // A particular eater: the wheat and oat bundle plus barley, so all three grasses are covered
      itemA = makeRec('bundle_nourishment', 'bundle_nourishment', 'rule_3plus_nourish', 'weekly');
      itemB = makeRec('barley', 'barley_enrichment', 'rule_3plus_barley', 'biweekly');
    } else if (d.needWheat) {
      // A cat prone to stress: wheat first, barley alongside (covers any mature or senior cat too)
      itemA = makeRec('wheat', 'wheat_enrichment', 'rule_3plus_anxiety', 'weekly');
      itemB = makeRec('barley', 'barley_enrichment', 'rule_3plus_barley', 'biweekly');
    } else if (d.hasDigestion && d.hasHairballs) {
      // From here on every pair includes barley, for any mature or senior cat
      itemA = makeRec('barley', 'barley_hairballs', 'rule_3plus_hairball', 'weekly');
      itemB = makeRec('oat', 'oat_digestion', 'rule_3plus_digestion', 'biweekly');
    } else if (d.hasHairballs) {
      itemA = makeRec('barley', 'barley_hairballs', 'rule_3plus_hairball', 'weekly');
      itemB = makeRec('wheat', 'wheat_enrichment', 'rule_3plus_vitamins', 'biweekly');
    } else if (d.hasDigestion) {
      itemA = makeRec('wheat', 'wheat_enrichment', 'rule_3plus_vitamins', 'weekly');
      itemB = makeRec('bundle_digestion', 'bundle_digestion', 'rule_3plus_digestion_bundle', 'weekly');
    } else if (d.hasBoredom) {
      itemA = makeRec('barley', 'barley_enrichment', 'rule_3plus_enrichment', 'weekly');
      itemB = makeRec('wheat', 'wheat_enrichment', 'rule_3plus_vitamins', 'biweekly');
    } else {
      itemA = makeRec('barley', 'barley_enrichment', 'rule_3plus_default_a', 'weekly');
      itemB = makeRec('wheat', 'wheat_default', 'rule_3plus_default_b', 'biweekly');
    }

    return {
      isMultiProduct: true,
      items: [itemA, itemB],
      ruleId: itemA.ruleId + '+' + itemB.ruleId
    };
  }

  function computeFields() {
    var cats = state.cats.slice(0, state.catCount);
    var concerns = cats.reduce(function (acc, c) { return acc.concat(c.concerns); }, []);
    var ages = cats.map(function (c) { return c.age; });
    var appetites = cats.map(function (c) { return c.appetite; });
    var experiences = cats.map(function (c) { return c.experience; });
    var anxieties = cats.map(function (c) { return c.anxiety; });
    var personalities = cats.map(function (c) { return c.personality; });

    var concernCounts = {};
    concerns.filter(function (c) { return c !== 'none'; }).forEach(function (c) { concernCounts[c] = (concernCounts[c] || 0) + 1; });
    var primaryConcern = Object.keys(concernCounts).sort(function (a, b) { return concernCounts[b] - concernCounts[a]; })[0] || '';

    return {
      catCount: state.catCount,
      primaryConcern: primaryConcern,
      hasHairballs: concerns.indexOf('hairballs') >= 0,
      hasDigestion: concerns.indexOf('digestion') >= 0,
      hasBoredom: concerns.indexOf('boredom') >= 0 || concerns.indexOf('plants') >= 0,
      noConcerns: concerns.length === 0 || (concerns.length === 1 && concerns[0] === 'none'),
      isPicky: appetites.some(function (a) { return a === 'picky'; }),
      isCautious: appetites.some(function (a) { return a === 'cautious'; }),
      hasKitten: ages.some(function (a) { return a === 'kitten'; }),
      hasSenior: ages.some(function (a) { return a === 'senior' || a === 'mature'; }),
      anyTriedDisliked: experiences.some(function (e) { return e === 'tried_disliked'; }),
      allLoveGrass: experiences.length > 0 && experiences.every(function (e) { return e === 'loves_it'; }),
      hasLongHair: cats.some(function (c) { return c.coat === 'long'; }),
      hasHighAnxiety: anxieties.some(function (a) { return a === 'moderate' || a === 'high'; }),
      hasShy: personalities.some(function (p) { return p === 'shy_hidden'; }),
      hasPlayful: personalities.some(function (p) { return p === 'playful_wild'; })
    };
  }

  function makeRec(product, copyKey, ruleId, recommendedFrequency) {
    var productBase = product;
    return {
      product: product,
      copyKey: copyKey,
      productBase: productBase,
      ruleId: ruleId,
      recommendedFrequency: recommendedFrequency || 'biweekly'
    };
  }

  // ─── PER-CAT INDIVIDUAL REASONING ──────────────────────

  var INDIVIDUAL_REASONS = {
    hairball_picky: 'Oat Grass for particular eaters, and it helps with hairballs',
    hairball: 'Barley Grass: broad leaves, and it helps with hairballs',
    digestion: 'Oat Grass: crisp grass for a careful start',
    tried_disliked: 'Oat Grass: crisp grass, worth another try',
    kitten_picky: 'Oat Grass for a young, particular eater',
    kitten: 'Wheat Grass: soft, fine grass for a young cat',
    stress_picky: 'Oat Grass for a particular appetite',
    stress: 'Wheat Grass: a daily ritual for cats prone to stress',
    loves_grass: 'Barley Grass for more in every bite',
    picky: 'Oat Grass: crisp grass for particular eaters',
    senior: 'Barley Grass for adult and senior cats',
    default: 'Wheat Grass: a daily ritual'
  };

  function getIndividualCatReason(cat) {
    var isPicky = cat.appetite === 'picky';
    var hasSenior = cat.age === 'senior' || cat.age === 'mature';
    var isKitten = cat.age === 'kitten';
    var hasHairballs = cat.concerns.indexOf('hairballs') >= 0;
    var hasDigestion = cat.concerns.indexOf('digestion') >= 0;
    var hasAnxiety = cat.anxiety === 'moderate' || cat.anxiety === 'high';

    if (hasSenior) return hasHairballs ? INDIVIDUAL_REASONS.hairball : INDIVIDUAL_REASONS.senior;
    if (cat.experience === 'tried_disliked') return INDIVIDUAL_REASONS.tried_disliked;
    if (isPicky) {
      if (hasHairballs) return INDIVIDUAL_REASONS.hairball_picky;
      if (hasAnxiety) return INDIVIDUAL_REASONS.stress_picky;
      return isKitten ? INDIVIDUAL_REASONS.kitten_picky : INDIVIDUAL_REASONS.picky;
    }
    if (hasAnxiety) return INDIVIDUAL_REASONS.stress;
    if (hasDigestion) return INDIVIDUAL_REASONS.digestion;
    if (hasHairballs) return INDIVIDUAL_REASONS.hairball;
    if (cat.experience === 'loves_it') return INDIVIDUAL_REASONS.loves_grass;
    return INDIVIDUAL_REASONS.default;
  }

  function buildPerCatProfiles() {
    if (state.catCount <= 1) return '';

    var cats = state.cats.slice(0, state.catCount);
    var reasons = cats.map(function (c) { return getIndividualCatReason(c); });

    // Check if all reasons are the same
    var allSame = reasons.every(function (r) { return r === reasons[0]; });
    if (allSame) return '';

    var html = '<div class="quiz-v2__cat-profiles">';
    cats.forEach(function (cat, i) {
      var name = cat.name || ('Cat ' + (i + 1));
      html += '<div class="quiz-v2__cat-profile">' +
        '<span class="quiz-v2__cat-profile-icon">\uD83D\uDC31</span>' +
        '<div class="quiz-v2__cat-profile-content">' +
          '<div class="quiz-v2__cat-profile-name">' + esc(name) + '</div>' +
          '<div class="quiz-v2__cat-profile-reason">' + esc(reasons[i]) + '</div>' +
        '</div></div>';
    });
    html += '</div>';
    return html;
  }

  // ─── EVENT HANDLERS ──────────────────────────────────────

  function handleClick(e) {
    var btn = e.target.closest('[data-action]');
    if (!btn) return;

    var action = btn.dataset.action;
    var value = btn.dataset.value;

    switch (action) {
      case 'start':
        state.step = STEPS.CAT_NAMES;
        render();
        fireEvent('quiz_started');
        break;

      case 'add_cat':
        if (state.catCount < 6) {
          state.catCount++;
          while (state.cats.length < state.catCount) state.cats.push(makeCat());
          render();
        }
        break;

      case 'remove_cat':
        var rmIdx = parseInt(btn.dataset.index, 10);
        if (state.catCount > 1) {
          state.cats.splice(rmIdx, 1);
          state.catCount--;
          render();
        }
        break;

      case 'select_age':
        setSelectionAndMaybeAdvance('age', value, action);
        break;

      case 'select_appetite':
        setSelectionAndMaybeAdvance('appetite', value, action);
        break;

      case 'select_diet':
        setSelectionAndMaybeAdvance('diet', value, action);
        break;

      case 'select_coat':
        setSelectionAndMaybeAdvance('coat', value, action);
        break;

      case 'toggle_concern': {
        var cat = state.cats[state.activeCat];
        var wasSelected = cat.concerns.indexOf(value) >= 0;
        if (value === 'none') {
          cat.concerns = ['none'];
        } else {
          cat.concerns = cat.concerns.filter(function (c) { return c !== 'none'; });
          var idx = cat.concerns.indexOf(value);
          if (idx >= 0) cat.concerns.splice(idx, 1);
          else cat.concerns.push(value);
        }
        render();
        fireEvent('quiz_answer_selected', {
          answer_step_key: 'concerns',
          answer_key: value,
          answer_label: getAnswerLabel('concerns', value),
          selected: value === 'none' ? true : !wasSelected,
          selected_count: cat.concerns.length
        });
        break;
      }

      case 'select_experience':
        setSelectionAndMaybeAdvance('experience', value, action);
        break;

      case 'select_anxiety':
        setSelectionAndMaybeAdvance('anxiety', value, action);
        break;

      case 'select_personality':
        setSelectionAndMaybeAdvance('personality', value, action);
        break;

      case 'set_frequency':
        state.frequency = value;
        render();
        fireEvent('quiz_frequency_selected', {
          subscription_frequency: value
        });
        break;

      case 'add_to_cart':
        addToCart(false);
        break;

      case 'add_trial':
        addToCart(true);
        break;

      case 'submit_email':
        submitEmail();
        break;

      case 'skip_email':
        state.emailSubmitted = true;
        render();
        fireEvent('quiz_email_skipped');
        break;

      case 'retake':
        retakeQuiz();
        break;
    }
  }

  function setSelectionAndMaybeAdvance(field, value, action) {
    var cat = state.cats[state.activeCat];
    if (!cat) return;

    clearPendingAutoAdvance();
    cat[field] = value;
    render();
    fireEvent('quiz_answer_selected', {
      answer_step_key: field,
      answer_key: value,
      answer_label: getAnswerLabel(field, value)
    });

    if (!AUTO_ADVANCE_ACTIONS[action]) return;
    if (CONFIG.enableAutoAdvance === false) return;
    if (!canAdvance()) return;

    var scheduledStep = state.step;
    var scheduledCat = state.activeCat;
    autoAdvanceTimer = setTimeout(function () {
      autoAdvanceTimer = null;
      if (state.step === STEPS.RESULTS || state.step === STEPS.ANALYZING) return;
      if (state.step !== scheduledStep || state.activeCat !== scheduledCat) return;
      handleNext();
    }, getAutoAdvanceDelay());
  }

  function handleInput(e) {
    var input = e.target;
    if (!input.dataset.input) return;

    if (input.dataset.input === 'cat_name') {
      var idx = parseInt(input.dataset.index, 10);
      if (state.cats[idx]) {
        state.cats[idx].name = input.value;
      }
      if (nextBtn) {
        nextBtn.disabled = !canAdvance();
        nextBtn.textContent = getNextButtonText();
      }
    }

    if (input.dataset.input === 'email') {
      state.email = input.value;
    }

    if (input.dataset.input === 'first_name') {
      state.firstName = input.value;
    }

    if (input.dataset.input === 'meta_ad_measurement') {
      state.metaAdMeasurementChecked = input.checked === true;
      if (state.metaAdMeasurementChecked) ensureConsentChallenge();
    }
  }

  // ─── STEP NAVIGATION ─────────────────────────────────────

  var STEP_ORDER = [
    STEPS.INTRO, STEPS.CAT_NAMES,
    STEPS.AGE, STEPS.APPETITE, STEPS.DIET, STEPS.COAT,
    STEPS.CONCERNS, STEPS.EXPERIENCE, STEPS.ANXIETY,
    STEPS.PERSONALITY, STEPS.ANALYZING, STEPS.RESULTS
  ];

  function getPerCatStepIndex(step) {
    return PER_CAT_STEPS.indexOf(step);
  }

  function isPerCatStep(step) {
    return PER_CAT_STEPS.indexOf(step) >= 0;
  }

  function handleNext() {
    clearPendingAutoAdvance();
    if (!canAdvance()) return;
    fireEvent('quiz_step_completed', { step: state.step, cat_index: state.activeCat });

    // Simplified mode for 4+ cats: skip per-cat detail steps
    if (state.step === STEPS.CAT_NAMES) {
      state.simplifiedMode = state.catCount >= 4;
      state.activeCat = 0;
      if (state.simplifiedMode) {
        // Go to simplified concerns-only for 4+ cats
        state.step = STEPS.CONCERNS;
      } else {
        state.step = STEPS.AGE;
      }
      render();
      return;
    }

    if (state.simplifiedMode) {
      // In simplified mode, after concerns go to analyzing
      if (state.step === STEPS.CONCERNS) {
        state.step = CONFIG.enableAnalyzing !== false ? STEPS.ANALYZING : STEPS.RESULTS;
        if (state.step === STEPS.RESULTS) {
          applyRecommendation(calculateRecommendation());
          var simpRec = state.recommendation;
          var simpProduct = simpRec.isMultiProduct
            ? simpRec.items.map(function (i) { return i.product; }).join('+')
            : simpRec.product;
          fireEvent('quiz_recommendation_shown', { product: simpProduct, reason: simpRec.ruleId, cat_count: state.catCount });
        }
      }
      render();
      return;
    }

    // Normal per-cat flow
    if (isPerCatStep(state.step)) {
      var pcIdx = getPerCatStepIndex(state.step);
      var isLastPerCatStep = pcIdx === PER_CAT_STEPS.length - 1;

      if (isLastPerCatStep) {
        // Done with this cat
        if (state.activeCat < state.catCount - 1) {
          state.activeCat++;
          state.step = PER_CAT_STEPS[0]; // back to age for next cat
        } else {
          // All cats done
          if (CONFIG.enableAnalyzing !== false) {
            state.step = STEPS.ANALYZING;
          } else {
            applyRecommendation(calculateRecommendation());
            var allDoneRec = state.recommendation;
            var allDoneProduct = allDoneRec.isMultiProduct
              ? allDoneRec.items.map(function (i) { return i.product; }).join('+')
              : allDoneRec.product;
            state.step = STEPS.RESULTS;
            fireEvent('quiz_recommendation_shown', { product: allDoneProduct, reason: allDoneRec.ruleId, cat_count: state.catCount });
          }
        }
      } else {
        state.step = PER_CAT_STEPS[pcIdx + 1];
      }
    }

    render();
  }

  function handleBack() {
    clearPendingAutoAdvance();
    var fromStep = state.step;
    var fromCat = state.activeCat;
    if (state.step === STEPS.CAT_NAMES) {
      state.step = STEPS.INTRO;
      render();
      fireEvent('quiz_back_clicked', { from_step_key: fromStep, from_cat_index: fromCat + 1 });
      return;
    }

    if (state.simplifiedMode && state.step === STEPS.CONCERNS) {
      state.step = STEPS.CAT_NAMES;
      render();
      fireEvent('quiz_back_clicked', { from_step_key: fromStep, from_cat_index: fromCat + 1 });
      return;
    }

    if (isPerCatStep(state.step)) {
      var pcIdx = getPerCatStepIndex(state.step);
      if (pcIdx === 0) {
        // First per-cat step
        if (state.activeCat > 0) {
          state.activeCat--;
          state.step = PER_CAT_STEPS[PER_CAT_STEPS.length - 1];
        } else {
          state.step = STEPS.CAT_NAMES;
        }
      } else {
        state.step = PER_CAT_STEPS[pcIdx - 1];
      }
    }

    render();
    fireEvent('quiz_back_clicked', { from_step_key: fromStep, from_cat_index: fromCat + 1 });
  }

  // ─── CART INTEGRATION ─────────────────────────────────────

  function addToCart(isTrial) {
    var rec = state.recommendation;
    if (!rec) return;

    var mainBtn = container.querySelector('[data-action="add_to_cart"]');
    var trialBtn = container.querySelector('[data-action="add_trial"]');
    var btn = isTrial ? trialBtn : mainBtn;

    if (btn) {
      btn.disabled = true;
      btn.classList.add('quiz-v2__add-btn--loading');
      btn.textContent = isTrial ? 'Adding trial...' : 'Adding...';
    }
    if (mainBtn && mainBtn !== btn) mainBtn.disabled = true;
    if (trialBtn && trialBtn !== btn) trialBtn.disabled = true;

    var cartData = getCartData(rec, isTrial);
    var recommendationPayload = getRecommendationAnalytics(rec, isTrial);
    recommendationPayload.is_trial = !!isTrial;
    recommendationPayload.value = getCartValue(rec, isTrial);
    recommendationPayload.currency = 'AED';
    fireEvent('quiz_recommendation_clicked', recommendationPayload);
    fireEvent('quiz_add_to_cart_started', recommendationPayload);

    if (!cartData.items || cartData.items.length === 0) {
      if (btn) {
        btn.textContent = 'Unavailable. Please try again.';
        btn.classList.remove('quiz-v2__add-btn--loading');
      }
      if (mainBtn) mainBtn.disabled = false;
      if (trialBtn) trialBtn.disabled = false;
      fireEvent('quiz_add_to_cart_failed', Object.assign({}, recommendationPayload, {
        error_type: 'empty_cart_payload'
      }));
      fireEvent('quiz_error', {
        error_type: 'empty_cart_payload',
        error_stage: 'cart'
      });
      return;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cartData)
    })
    .then(function (res) {
      if (!res.ok) throw new Error('Cart error');
      return res.json();
    })
    .then(function () {
      state.addedToCart = true;
      var ga4Items = buildGa4Items(rec, isTrial);
      fireEvent('quiz_add_to_cart_success', recommendationPayload);
      emitQuizEvent('add_to_cart', {
        currency: 'AED',
        value: recommendationPayload.value,
        items: ga4Items
      }, { ecommerce: true });

      if (!state.emailSubmitted && state.email) {
        sendKlaviyo({ isTrial: isTrial });
      }

      document.body.classList.remove('quiz-v2-active');

      var redirect = CONFIG.redirectAfterAdd || 'checkout';
      if (redirect === 'checkout') {
        fireEvent('quiz_checkout_clicked', recommendationPayload);
        emitQuizEvent('begin_checkout', {
          currency: 'AED',
          value: recommendationPayload.value,
          items: ga4Items
        }, { ecommerce: true });
        window.location.href = '/checkout';
      } else if (redirect === 'cart') {
        window.location.href = '/cart';
      } else {
        if (btn) {
          btn.textContent = 'Added!';
          btn.classList.remove('quiz-v2__add-btn--loading');
          btn.disabled = false;
        }
        if (mainBtn && mainBtn !== btn) mainBtn.disabled = false;
        if (trialBtn && trialBtn !== btn) trialBtn.disabled = false;
      }
    })
    .catch(function (err) {
      console.error('[Quiz V2] Cart error:', err);
      fireEvent('quiz_add_to_cart_failed', Object.assign({}, recommendationPayload, {
        error_type: 'cart_request_failed',
        error_message: err && err.message ? err.message : 'Cart request failed'
      }));
      fireEvent('quiz_error', {
        error_type: 'cart_request_failed',
        error_stage: 'cart'
      });
      if (btn) {
        btn.textContent = 'Something went wrong. Try again.';
        btn.classList.remove('quiz-v2__add-btn--loading');
        btn.disabled = false;
        setTimeout(function () {
          btn.textContent = isTrial
            ? 'Prefer a one-time trial box instead'
            : ('Start ' + (state.cats[0] ? state.cats[0].name : 'your cat') + "'s subscription now");
        }, 3000);
      }
      if (mainBtn && mainBtn !== btn) mainBtn.disabled = false;
      if (trialBtn && trialBtn !== btn) trialBtn.disabled = false;
    });
  }

  function getCartData(rec, isTrial) {
    return {
      items: getCartItems(rec, isTrial),
      attributes: buildCartAttributes(rec, isTrial)
    };
  }

  function getCartItems(rec, isTrial) {
    // Multi-product: build items array from each rec item
    if (rec.isMultiProduct && !isTrial) {
      var allItems = [];
      for (var mi = 0; mi < rec.items.length; mi++) {
        var multiItem = rec.items[mi];
        var multiCartItems = getCartItemsForSingleRec(multiItem, multiItem.recommendedFrequency || 'weekly');
        if (multiCartItems && multiCartItems.length > 0) {
          allItems = allItems.concat(multiCartItems);
        }
      }
      return allItems;
    }

    // For trial on multi-product, pick the primary item
    var effectiveRec = rec.isMultiProduct ? rec.items[0] : rec;
    return getCartItemsForSingleRec(effectiveRec, state.frequency, isTrial);
  }

  function getCartDataForSingleRec(rec, freq, isTrial) {
    return {
      items: getCartItemsForSingleRec(rec, freq, isTrial),
      attributes: buildCartAttributes(rec, isTrial)
    };
  }

  function getCartItemsForSingleRec(rec, freq, isTrial) {
    var product = rec.product;
    var variantId, sellingPlanId;

    if (isTrial) {
      var trialProductMap = {
        barley: 'barley',
        oat: 'oat',
        wheat: 'wheat',
        bundle_digestion: 'oat',
        bundle_nourishment: 'oat',
        bundle_vitality: 'barley',
        bundle_complete: 'wheat'
      };
      var singleKey = trialProductMap[product] || 'wheat';
      var p = PRODUCTS[singleKey];
      if (!p || !p.variant_id) {
        console.error('[Quiz V2] Missing product config for trial:', singleKey);
        return [];
      }
      var trialVariantId = parseInt(p.variant_id, 10);
      if (!trialVariantId) {
        console.error('[Quiz V2] Invalid trial variant id for:', singleKey);
        return [];
      }
      return [{ id: trialVariantId, quantity: 1 }];
    }

    if (product === 'bundle_complete') {
      variantId = CONFIG.variantComplete;
      sellingPlanId = getBundleSellingPlanId('bundle_complete', freq, CONFIG.sellingPlanComplete);
    } else if (product === 'bundle_digestion') {
      variantId = CONFIG.variantDigestion;
      sellingPlanId = getBundleSellingPlanId('bundle_digestion', freq, CONFIG.sellingPlanDigestion);
    } else if (product === 'bundle_vitality') {
      variantId = CONFIG.variantVitality;
      sellingPlanId = getBundleSellingPlanId('bundle_vitality', freq, CONFIG.sellingPlanVitality);
    } else if (product === 'bundle_nourishment') {
      variantId = CONFIG.variantNourishment;
      sellingPlanId = getBundleSellingPlanId('bundle_nourishment', freq, CONFIG.sellingPlanNourishment);
    } else {
      var key = product.charAt(0).toUpperCase() + product.slice(1);
      if (freq === 'weekly') {
        variantId = CONFIG['variant' + key + 'Weekly'];
      } else {
        variantId = CONFIG['variant' + key + 'Biweekly'];
      }
      sellingPlanId = getSingleSellingPlanId(product, freq);
    }

    var parsedVariantId = parseInt(variantId, 10);
    if (!parsedVariantId) {
      var fallbackProduct = PRODUCTS[product];
      parsedVariantId = fallbackProduct && fallbackProduct.variant_id ? parseInt(fallbackProduct.variant_id, 10) : 0;
    }
    if (!parsedVariantId) {
      console.error('[Quiz V2] Missing variant for product:', product);
      return [];
    }

    var item = { id: parsedVariantId, quantity: getQuantity(product) };
    var parsedSellingPlan = parseInt(sellingPlanId, 10);
    if (parsedSellingPlan) item.selling_plan = parsedSellingPlan;

    return [item];
  }

  function getBundleSellingPlanId(productKey, frequency, fallbackPlan) {
    var fallbackId = parseId(fallbackPlan);
    var dynamicId = findSellingPlanId(productKey, frequency);
    return dynamicId || fallbackId;
  }

  function getQuantity(product) {
    if (isBundleProduct(product)) return 1;
    if (state.catCount <= 1) return 1;
    if (state.catCount <= 3) return 2;
    return 3;
  }

  function buildCartAttributes(rec, isTrial) {
    return buildCartAttributeSchema(rec, isTrial);
  }

  // ─── KLAVIYO INTEGRATION ──────────────────────────────────

  function sendKlaviyo(opts) {
    opts = opts || {};
    if (!state.email) return;
    if (state.emailCapturedTracked) return; // dedupe guard

    var _learnq = window._learnq || [];

    // Calculate primary concern (most frequent across all cats)
    var concernCounts = {};
    state.cats.forEach(function(c) {
      (c.concerns || []).forEach(function(concern) {
        concernCounts[concern] = (concernCounts[concern] || 0) + 1;
      });
    });
    var primaryConcern = Object.keys(concernCounts).sort(function(a, b) {
      return concernCounts[b] - concernCounts[a];
    })[0] || 'general_wellness';

    var productStr = getProductString(state.recommendation);
    var productFrequencies = getProductFrequencyString(state.recommendation);

    // Identify with profile properties
    var identifyProps = {
      '$email': state.email,
      'quiz_version': getQuizVersion(),
      'quiz_session_id': state.quizSessionId,
      'quiz_product': productStr,
      'quiz_product_frequencies': productFrequencies,
      'quiz_cat_count': state.catCount,
      'quiz_cat_names': getCatNamesString(),
      'quiz_frequency': state.frequency,
      'quiz_primary_concern': primaryConcern,
      'quiz_completed_at': state.quizCompletedAt
    };
    if (state.firstName) identifyProps['$first_name'] = state.firstName;
    _learnq.push(['identify', identifyProps]);

    // Track identified "Quiz Email Captured" event
    _learnq.push(['track', 'Quiz Email Captured', {
      quiz_session_id: state.quizSessionId,
      quiz_version: getQuizVersion(),
      quiz_product: productStr,
      quiz_product_frequencies: productFrequencies,
      quiz_cat_count: state.catCount,
      quiz_cat_names: getCatNamesString(),
      quiz_frequency: state.frequency,
      quiz_primary_concern: primaryConcern,
      quiz_concerns: state.cats.reduce(function(a, c) { return a.concat(c.concerns); }, []).join(','),
      quiz_cat_data: JSON.stringify(state.cats.map(function(c) {
        return { name: c.name, age: c.age, concerns: c.concerns };
      })),
      quiz_is_trial: String(!!opts.isTrial),
      quiz_completed_at: state.quizCompletedAt
    }]);

    state.emailSubmitted = true;
    state.emailCapturedTracked = true;
  }

  function submitEmail() {
    var nameInput = container.querySelector('[data-input="first_name"]');
    var emailInput = container.querySelector('[data-input="email"]');
    if (!emailInput || !emailInput.value || emailInput.value.indexOf('@') < 0) {
      if (emailInput) emailInput.classList.add('quiz-v2__input--error');
      return;
    }
    state.email = emailInput.value;
    if (nameInput && nameInput.value) state.firstName = nameInput.value;

    sendKlaviyo({ isTrial: false });
    fireEvent('quiz_email_submitted', {
      quiz_session_id: state.quizSessionId,
      email_captured: true
    });

    // Update cart attribute to flag email was captured (no PII)
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { quiz_email_captured: 'true' } })
    }).catch(function(e) { console.warn('Cart attr email update failed:', e); });

    // Do not hold the existing capture flow. Only the optional Meta candidate waits
    // for the session-bound challenge; a failed challenge sends the base payload.
    sendLeadAfterConsentResolution();

    render();
  }

  function getRecommendationEntries(rec) {
    if (!rec) return [];
    return rec.isMultiProduct && rec.items ? rec.items : [rec];
  }

  function getRecommendationAnalytics(rec, isTrial) {
    var entries = getRecommendationEntries(rec);
    var cartItems = rec ? getCartItems(rec, !!isTrial) : [];
    var handles = [];
    var products = [];
    var variantIds = [];
    var sellingPlanIds = [];

    entries.forEach(function(entry, index) {
      var product = PRODUCTS[entry.product] || {};
      var cartItem = cartItems[index] || cartItems[0] || {};
      products.push(entry.product);
      handles.push(product.handle || entry.product);
      if (cartItem.id != null) variantIds.push(String(cartItem.id));
      if (cartItem.selling_plan != null) sellingPlanIds.push(String(cartItem.selling_plan));
    });

    return {
      recommendation_type: rec && rec.isMultiProduct ? 'multi_product' : 'single',
      recommended_products: products,
      recommended_handles: handles,
      recommended_variant_ids: variantIds,
      recommended_selling_plan_ids: sellingPlanIds,
      subscription_frequency: state.frequency,
      quiz_product_frequencies: getProductFrequencyString(rec),
      rule_id: rec ? rec.ruleId : ''
    };
  }

  function getProductPriceByVariantId(variantId) {
    var parsedVariantId = parseId(variantId);
    var keys = Object.keys(PRODUCTS || {});
    for (var i = 0; i < keys.length; i++) {
      var product = PRODUCTS[keys[i]];
      if (parseId(product && product.variant_id) === parsedVariantId) {
        return parseId(product.price) || 0;
      }
    }
    return 0;
  }

  function getCartValue(rec, isTrial) {
    if (!rec) return 0;
    if (isTrial) {
      var trialItems = getCartItems(rec, true);
      var firstItem = trialItems && trialItems[0];
      if (!firstItem) return 0;
      return Math.round(getProductPriceByVariantId(firstItem.id)) / 100;
    }

    var entries = getRecommendationEntries(rec);
    var total = 0;
    entries.forEach(function(entry) {
      var frequency = entry.recommendedFrequency || state.frequency;
      var cents = getDisplayedPrice(entry.product, frequency);
      if (cents == null) cents = getProductPriceByVariantId(getVariantIdForPrice(entry.product, frequency));
      total += cents || 0;
    });
    return Math.round(total) / 100;
  }

  function buildGa4Items(rec, isTrial) {
    var cartItems = rec ? getCartItems(rec, !!isTrial) : [];
    var entries = getRecommendationEntries(rec);
    return cartItems.map(function(item, index) {
      var entry = entries[index] || entries[0] || {};
      var product = PRODUCTS[entry.product] || {};
      var frequency = entry.recommendedFrequency || state.frequency;
      return {
        item_id: String(item.id),
        item_name: getDisplayProductTitle(entry.product, product.title || entry.product || 'Cat Grass Box'),
        item_category: 'Cat Grass',
        item_variant: entry.product || '',
        price: getCartValue(entry, isTrial),
        quantity: item.quantity || 1,
        subscription_frequency: isTrial ? 'one_time_trial' : frequency,
        selling_plan_id: item.selling_plan ? String(item.selling_plan) : ''
      };
    });
  }

  function getCurrentStepAnswerValue() {
    var cat = state.cats[state.activeCat];
    if (!cat) return '';
    if (state.step === STEPS.AGE) return cat.age;
    if (state.step === STEPS.APPETITE) return cat.appetite;
    if (state.step === STEPS.DIET) return cat.diet;
    if (state.step === STEPS.COAT) return cat.coat;
    if (state.step === STEPS.CONCERNS) return cat.concerns.join('+');
    if (state.step === STEPS.EXPERIENCE) return cat.experience;
    if (state.step === STEPS.ANXIETY) return cat.anxiety;
    if (state.step === STEPS.PERSONALITY) return cat.personality;
    return '';
  }

  function getAnswerLabel(field, value) {
    var options = {
      age: {
        kitten: 'Kitten',
        young_adult: 'Young adult',
        mature: 'Mature adult',
        senior: 'Senior'
      },
      appetite: {
        adventurous: 'Adventurous eater',
        cautious: 'Cautious eater',
        picky: 'Picky eater'
      },
      coat: {
        short: 'Short hair',
        long: 'Long hair',
        hairless: 'Hairless'
      },
      experience: {
        first_time: 'First time',
        tried_disliked: 'Tried before and disliked it',
        loves_it: 'Already loves grass'
      }
    };
    if (field === 'diet') {
      var diet = DIET_OPTIONS.filter(function(option) { return option.value === value; })[0];
      return diet ? diet.label : value;
    }
    if (field === 'personality') {
      var personality = PERSONALITY_OPTIONS.filter(function(option) { return option.value === value; })[0];
      return personality ? personality.label : value;
    }
    if (field === 'anxiety') {
      var anxiety = ANXIETY_OPTIONS.filter(function(option) { return option.value === value; })[0];
      return anxiety ? anxiety.label : value;
    }
    if (field === 'concerns') {
      var concern = CONCERNS_OPTIONS.filter(function(option) { return option.value === value; })[0];
      return concern ? concern.label : value;
    }
    return options[field] && options[field][value] ? options[field][value] : value;
  }

  function trackStepViewed() {
    var key = state.step + ':' + state.activeCat;
    if (key === lastTrackedStepViewKey) return;
    lastTrackedStepViewKey = key;
    fireEvent('quiz_step_viewed', {
      viewed_step_key: state.step,
      viewed_step_index: getStepIndex(state.step),
      viewed_step_type: getStepType(state.step)
    });
  }

  function getVisibleFactKey() {
    if (!container || !container.querySelector('[data-q2-fact]')) return '';
    return state.step + ':' + state.activeCat + ':' + getCurrentStepAnswerValue();
  }

  function trackVisibleFact() {
    var factKey = getVisibleFactKey();
    if (!factKey || factKey === lastTrackedFactKey) return;
    lastTrackedFactKey = factKey;
    fireEvent('quiz_fact_shown', {
      fact_key: state.step + '_' + getCurrentStepAnswerValue(),
      answer_key: getCurrentStepAnswerValue()
    });
  }

  function trackResultViewed() {
    if (state.resultViewedTracked) return;
    state.resultViewedTracked = true;
    var rec = state.recommendation;
    var payload = getRecommendationAnalytics(rec, false);
    payload.value = getCartValue(rec, false);
    payload.currency = 'AED';
    fireEvent('quiz_result_viewed', payload);
  }

  // ─── ANALYTICS ────────────────────────────────────────────

  function getAnalyticsExperience() {
    return CONFIG.analyticsExperience || 'start_page';
  }

  function getViewportBucket() {
    var width = window.innerWidth || (document.documentElement && document.documentElement.clientWidth) || 0;
    return width && width < 768 ? 'mobile' : 'desktop';
  }

  function getCatCountBucket() {
    if (state.catCount >= 4) return '4_plus';
    return String(state.catCount || 1);
  }

  function getFunnelStage(step) {
    if (step === STEPS.INTRO) return 'intro';
    if (step === STEPS.RESULTS) return 'result';
    if (step === STEPS.ANALYZING) return 'analyzing';
    return 'question';
  }

  function getStepIndex(step) {
    if (step === STEPS.INTRO) return 0;
    if (step === STEPS.CAT_NAMES) return 1;
    if (isPerCatStep(step)) return 2 + getPerCatStepIndex(step);
    if (step === STEPS.ANALYZING) return 10;
    if (step === STEPS.RESULTS) return 11;
    return 0;
  }

  function getStepType(step) {
    if (step === STEPS.INTRO) return 'intro';
    if (step === STEPS.CAT_NAMES) return 'text';
    if (step === STEPS.CONCERNS) return 'multi_choice';
    if (step === STEPS.ANALYZING) return 'loading';
    if (step === STEPS.RESULTS) return 'result';
    return 'single_choice';
  }

  function isBlockedAnalyticsKey(key) {
    return {
      email: true,
      first_name: true,
      firstName: true,
      name: true,
      names: true,
      cat_name: true,
      cat_names: true,
      quiz_cat_names: true,
      cat_data: true,
      quiz_cat_data: true
    }[key] === true;
  }

  function looksLikeEmail(value) {
    return typeof value === 'string' && /[^\s@]+@[^\s@]+\.[^\s@]+/.test(value);
  }

  function sanitizeQuizEventValue(value) {
    if (value == null) return value;
    if (looksLikeEmail(value)) return undefined;
    if (Array.isArray(value)) {
      return value.map(function(item) {
        return sanitizeQuizEventValue(item);
      }).filter(function(item) {
        return item !== undefined;
      });
    }
    if (typeof value === 'object') {
      var cleaned = {};
      Object.keys(value).forEach(function(key) {
        if (isBlockedAnalyticsKey(key)) return;
        var cleanedValue = sanitizeQuizEventValue(value[key]);
        if (cleanedValue !== undefined) cleaned[key] = cleanedValue;
      });
      return cleaned;
    }
    return value;
  }

  function sanitizeQuizEventPayload(data) {
    var cleaned = {};
    data = data || {};
    Object.keys(data).forEach(function(key) {
      if (isBlockedAnalyticsKey(key)) return;
      var cleanedValue = sanitizeQuizEventValue(data[key]);
      if (cleanedValue !== undefined) cleaned[key] = cleanedValue;
    });
    return cleaned;
  }

  function buildQuizEventPayload(name, data) {
    var payload = {
      event: name,
      quiz_name: 'sprouties_quiz',
      quiz_experience: getAnalyticsExperience(),
      quiz_session_id: getQuizSessionId(),
      funnel_stage: getFunnelStage(state.step),
      step_index: getStepIndex(state.step),
      step_key: state.step,
      step_type: getStepType(state.step),
      active_cat_index: state.activeCat + 1,
      cat_count: state.catCount,
      cat_count_bucket: getCatCountBucket(),
      elapsed_ms: Math.max(0, Date.now() - quizStartedAt),
      viewport: getViewportBucket(),
      page_path: window.location ? window.location.pathname : ''
    };

    var safeData = sanitizeQuizEventPayload(data);
    Object.keys(safeData).forEach(function(key) {
      payload[key] = safeData[key];
    });

    return payload;
  }

  function emitQuizEvent(name, data, options) {
    options = options || {};
    if (CONFIG.analyticsEnabled === false) return null;

    var payload = buildQuizEventPayload(name, data);

    if (typeof gtag === 'function') {
      var gtagData = {};
      Object.keys(payload).forEach(function(key) {
        if (key !== 'event') gtagData[key] = payload[key];
      });
      gtag('event', name, gtagData);
    }

    window.dataLayer = window.dataLayer || [];
    if (typeof window.dataLayer.push === 'function') {
      if (options.ecommerce) {
        window.dataLayer.push({ ecommerce: null });
        payload.ecommerce = {
          currency: payload.currency || 'AED',
          value: payload.value || 0,
          items: payload.items || []
        };
      }
      window.dataLayer.push(payload);
    }

    if (CONFIG.analyticsDebug && window.console && typeof window.console.log === 'function') {
      window.console.log('[Quiz Analytics]', payload);
    }

    try {
      document.dispatchEvent(new CustomEvent('quiz:' + name, { detail: payload }));
    } catch (e) {}

    return payload;
  }

  function fireEvent(name, data, options) {
    return emitQuizEvent(name, data, options);
  }

  // ─── STATE PERSISTENCE ────────────────────────────────────

  function saveState() {
    try {
      localStorage.setItem(QUIZ_STATE_STORAGE_KEY, JSON.stringify({
        step: state.step,
        catCount: state.catCount,
        cats: state.cats,
        activeCat: state.activeCat,
        simplifiedMode: state.simplifiedMode,
        frequency: state.frequency,
        timestamp: Date.now()
      }));
    } catch (e) {}
  }

  function restoreState() {
    try {
      var saved = JSON.parse(localStorage.getItem(QUIZ_STATE_STORAGE_KEY));
      if (!saved) return;
      if (Date.now() - saved.timestamp > 30 * 60 * 1000) {
        localStorage.removeItem(QUIZ_STATE_STORAGE_KEY);
        return;
      }
      if (saved.step === STEPS.RESULTS || saved.step === STEPS.ANALYZING) return;
      if (saved.step !== STEPS.INTRO) {
        state.step = saved.step;
        state.catCount = saved.catCount || 1;
        state.cats = saved.cats || [makeCat()];
        state.activeCat = saved.activeCat || 0;
        state.simplifiedMode = saved.simplifiedMode || false;
        state.frequency = saved.frequency || 'biweekly';
      }
    } catch (e) {
      localStorage.removeItem(QUIZ_STATE_STORAGE_KEY);
    }
  }

  function retakeQuiz() {
    clearPendingAutoAdvance();
    fireEvent('quiz_retake_clicked', {
      from_step_key: state.step
    });
    state = createInitialQuizState();
    consentChallengePromise = null;
    lastTrackedStepViewKey = '';
    lastTrackedFactKey = '';
    quizStartedAt = Date.now();
    try {
      if (window.sessionStorage) window.sessionStorage.removeItem(QUIZ_SESSION_STORAGE_KEY);
    } catch (e) {}
    localStorage.removeItem(QUIZ_STATE_STORAGE_KEY);
    if (CONFIG.fullscreen !== false) {
      document.body.classList.add('quiz-v2-active');
    }
    render();
  }

  // ─── HELPERS ──────────────────────────────────────────────

  function getCatNamesString() {
    var names = state.cats.slice(0, state.catCount).map(function (c) { return c.name; }).filter(Boolean);
    if (names.length === 0) return 'your cat';
    if (names.length === 1) return names[0];
    if (names.length === 2) return names[0] + ' and ' + names[1];
    return names[0] + ', ' + names[1] + ', and ' + (names.length - 2) + ' more';
  }

  function formatPrice(cents) {
    if (!cents) return 'AED 0';
    return 'AED ' + (cents / 100).toFixed(0);
  }

  function isBundleProduct(product) {
    return typeof product === 'string' && product.indexOf('bundle_') === 0;
  }

  function parseId(val) {
    var n = parseInt(val, 10);
    return isNaN(n) ? 0 : n;
  }

  function getPriceCacheKey(variantId, sellingPlanId) {
    return String(variantId || 0) + '|' + String(sellingPlanId || 0);
  }

  function getPlanSearchText(plan) {
    if (!plan) return '';
    var parts = [];
    if (plan.name) parts.push(String(plan.name));
    if (plan.options && plan.options.length) parts.push(plan.options.join(' '));
    if (plan.description) parts.push(String(plan.description));
    return parts.join(' ').toLowerCase();
  }

  function planMatchesFrequency(plan, frequency) {
    var text = getPlanSearchText(plan).replace(/[-_]+/g, ' ');
    if (!text) return false;
    if (frequency === 'weekly') {
      return (text.indexOf('weekly') >= 0 && text.indexOf('biweekly') < 0 && text.indexOf('bi weekly') < 0) ||
        text.indexOf('every week') >= 0 ||
        text.indexOf('7 day') >= 0;
    }
    if (frequency === 'biweekly') {
      return text.indexOf('biweekly') >= 0 ||
        text.indexOf('bi weekly') >= 0 ||
        text.indexOf('fortnight') >= 0 ||
        text.indexOf('every 2 week') >= 0 ||
        text.indexOf('every two week') >= 0 ||
        text.indexOf('14 day') >= 0;
    }
    return false;
  }

  function findSellingPlanId(productKey, frequency) {
    var p = PRODUCTS[productKey];
    var groups = p && p.selling_plans ? p.selling_plans : [];
    for (var g = 0; g < groups.length; g++) {
      var plans = groups[g] && groups[g].selling_plans ? groups[g].selling_plans : [];
      for (var i = 0; i < plans.length; i++) {
        var plan = plans[i];
        if (planMatchesFrequency(plan, frequency) && parseId(plan.id)) {
          return parseId(plan.id);
        }
      }
    }
    return 0;
  }

  function getSingleSellingPlanId(product, frequency) {
    var dynamicId = findSellingPlanId(product, frequency);
    if (dynamicId) return dynamicId;

    var key = product.charAt(0).toUpperCase() + product.slice(1);
    if (frequency === 'weekly') return parseId(CONFIG['sellingPlan' + key + 'Weekly']);
    return parseId(CONFIG['sellingPlan' + key + 'Biweekly']);
  }

  function getSellingPlanIdForPrice(product, frequency) {
    if (!product) return 0;

    if (isBundleProduct(product)) {
      if (product === 'bundle_complete') return getBundleSellingPlanId(product, frequency, CONFIG.sellingPlanComplete);
      if (product === 'bundle_digestion') return getBundleSellingPlanId(product, frequency, CONFIG.sellingPlanDigestion);
      if (product === 'bundle_vitality') return getBundleSellingPlanId(product, frequency, CONFIG.sellingPlanVitality);
      if (product === 'bundle_nourishment') return getBundleSellingPlanId(product, frequency, CONFIG.sellingPlanNourishment);
      return 0;
    }

    return getSingleSellingPlanId(product, frequency);
  }

  function getPlanPriceFromGroups(productKey, sellingPlanId, baseCents) {
    var p = PRODUCTS[productKey];
    var groups = p && p.selling_plans ? p.selling_plans : [];
    for (var g = 0; g < groups.length; g++) {
      var plans = groups[g] && groups[g].selling_plans ? groups[g].selling_plans : [];
      for (var i = 0; i < plans.length; i++) {
        var plan = plans[i];
        if (parseId(plan.id) !== parseId(sellingPlanId)) continue;

        var directPrice = parseId(plan.price || plan.per_delivery_price);
        if (directPrice) return directPrice;

        var adjustments = plan.price_adjustments || [];
        if (!adjustments.length || !baseCents) return 0;

        var adj = adjustments[0] || {};
        var rawType = String(adj.value_type || adj.adjustment_type || '').toLowerCase();
        var rawValue = adj.value;
        if (rawValue == null) rawValue = adj.adjustment_value;
        if (rawValue == null) rawValue = adj.adjustment_amount;
        if (rawValue == null) rawValue = adj.amount;
        if (rawValue == null) rawValue = adj.price;
        var value = parseFloat(rawValue);
        if (isNaN(value)) return 0;

        if (rawType.indexOf('percent') >= 0) {
          var pct = value <= 1 ? value * 100 : value;
          return Math.max(0, Math.round(baseCents * ((100 - pct) / 100)));
        }

        if (rawType.indexOf('fixed') >= 0 || rawType.indexOf('amount') >= 0) {
          return Math.max(0, baseCents - Math.round(value));
        }

        if (rawType.indexOf('price') >= 0) {
          return Math.max(0, Math.round(value));
        }

        // Unknown policy shape: keep trying via product JSON allocations.
        return 0;
      }
    }
    return 0;
  }

  function getVariantIdForPrice(product, frequency) {
    if (!product) return 0;

    if (product === 'bundle_complete') return parseId(CONFIG.variantComplete);
    if (product === 'bundle_digestion') return parseId(CONFIG.variantDigestion);
    if (product === 'bundle_vitality') return parseId(CONFIG.variantVitality);
    if (product === 'bundle_nourishment') return parseId(CONFIG.variantNourishment);

    var key = product.charAt(0).toUpperCase() + product.slice(1);
    if (frequency === 'weekly') return parseId(CONFIG['variant' + key + 'Weekly']);
    return parseId(CONFIG['variant' + key + 'Biweekly']);
  }

  function fetchVariantPrice(variantId, cacheKey) {
    PRICE_CACHE[cacheKey] = { status: 'loading' };
    fetch('/variants/' + variantId + '.js')
      .then(function (res) {
        if (!res.ok) throw new Error('Variant price fetch failed');
        return res.json();
      })
      .then(function (variant) {
        PRICE_CACHE[cacheKey] = {
          status: 'ready',
          cents: parseId(variant && variant.price)
        };
        if (state.step === STEPS.RESULTS) render();
      })
      .catch(function (err) {
        PRICE_CACHE[cacheKey] = { status: 'error' };
        console.warn('[Quiz V2] Price fallback unavailable for variant', variantId, err);
      });
  }

  function loadProductJson(productKey) {
    var cache = PRODUCT_JSON_CACHE[productKey];
    if (cache && cache.status === 'ready') return Promise.resolve(cache.data);
    if (cache && cache.status === 'loading') return cache.promise;

    var p = PRODUCTS[productKey];
    var url = p && p.url ? String(p.url) : '';
    if (!url) return Promise.reject(new Error('Missing product URL'));

    var promise = fetch(url + '.js')
      .then(function (res) {
        if (!res.ok) throw new Error('Product JSON fetch failed');
        return res.json();
      })
      .then(function (json) {
        PRODUCT_JSON_CACHE[productKey] = { status: 'ready', data: json };
        return json;
      })
      .catch(function (err) {
        PRODUCT_JSON_CACHE[productKey] = { status: 'error' };
        throw err;
      });

    PRODUCT_JSON_CACHE[productKey] = { status: 'loading', promise: promise };
    return promise;
  }

  function getPlanAllocationPrice(variant, sellingPlanId) {
    if (!variant || !sellingPlanId) return 0;
    var allocs = variant.selling_plan_allocations || [];
    for (var i = 0; i < allocs.length; i++) {
      var alloc = allocs[i] || {};
      var allocPlanId = parseId(alloc.selling_plan_id || (alloc.selling_plan && alloc.selling_plan.id));
      if (allocPlanId !== parseId(sellingPlanId)) continue;
      var price = parseId(alloc.per_delivery_price || alloc.price);
      if (price) return price;
    }
    return 0;
  }

  function fetchPlanPrice(productKey, variantId, sellingPlanId, cacheKey, basePrice) {
    PRICE_CACHE[cacheKey] = { status: 'loading' };
    loadProductJson(productKey)
      .then(function (productJson) {
        var variants = productJson && productJson.variants ? productJson.variants : [];
        var variant = null;
        for (var i = 0; i < variants.length; i++) {
          if (parseId(variants[i].id) === parseId(variantId)) {
            variant = variants[i];
            break;
          }
        }

        var allocPrice = getPlanAllocationPrice(variant, sellingPlanId);
        if (allocPrice) {
          PRICE_CACHE[cacheKey] = { status: 'ready', cents: allocPrice };
          if (state.step === STEPS.RESULTS) render();
          return;
        }

        // Fallback: try adjustment math using live variant base.
        var liveBase = parseId(variant && variant.price) || basePrice;
        var adjusted = getPlanPriceFromGroups(productKey, sellingPlanId, liveBase);
        if (adjusted) {
          PRICE_CACHE[cacheKey] = { status: 'ready', cents: adjusted };
          if (state.step === STEPS.RESULTS) render();
          return;
        }

        PRICE_CACHE[cacheKey] = { status: 'error' };
        console.warn('[Quiz V2] Missing selling plan allocation price', productKey, sellingPlanId);
      })
      .catch(function (err) {
        PRICE_CACHE[cacheKey] = { status: 'error' };
        console.warn('[Quiz V2] Plan price fetch failed', productKey, sellingPlanId, err);
      });
  }

  function getDisplayedPrice(product, frequency) {
    var variantId = getVariantIdForPrice(product, frequency);
    var sellingPlanId = getSellingPlanIdForPrice(product, frequency);
    var cacheKey = getPriceCacheKey(variantId, sellingPlanId);
    var p = PRODUCTS[product] || {};
    var basePrice = parseId(p.price);

    if (!variantId) {
      if (!basePrice) {
        console.warn('[Quiz V2] Missing variant/product price for', product, frequency);
        return null;
      }
      return basePrice;
    }

    var cached = PRICE_CACHE[cacheKey];
    if (cached && cached.status === 'ready') return cached.cents;

    if (!cached) {
      if (sellingPlanId) {
        var groupPrice = getPlanPriceFromGroups(product, sellingPlanId, basePrice);
        if (groupPrice) {
          PRICE_CACHE[cacheKey] = { status: 'ready', cents: groupPrice };
          return groupPrice;
        }
        fetchPlanPrice(product, variantId, sellingPlanId, cacheKey, basePrice);
      } else {
        fetchVariantPrice(variantId, cacheKey);
      }
    }

    if (cached && cached.status === 'error' && !sellingPlanId && basePrice) {
      return basePrice;
    }

    return null;
  }

  function buildMultiCatSubtitle() {
    var cats = state.cats.slice(0, state.catCount);
    var concerns = [];
    cats.forEach(function (c) {
      if (c.concerns.indexOf('hairballs') >= 0 && concerns.indexOf('hairball concerns') < 0) concerns.push('hairball concerns');
      if (c.concerns.indexOf('digestion') >= 0 && concerns.indexOf('first steps with grass') < 0) concerns.push('first steps with grass');
      if (c.appetite === 'picky' && c.age !== 'mature' && c.age !== 'senior' && concerns.indexOf('picky appetites') < 0) concerns.push('picky appetites');
    });
    if (concerns.length > 0) {
      return 'Covering ' + concerns.join(', ') + ' across the group.';
    }
    return 'Personalized for all ' + state.catCount + ' cats.';
  }

  function buildResultSubtitle(cat, name) {
    if (!cat) return 'Personalized for ' + name + '.';
    var parts = [];
    if (cat.concerns.indexOf('hairballs') >= 0) parts.push('hairball concerns');
    else if (cat.concerns.indexOf('digestion') >= 0) parts.push('first steps with grass');
    else if (cat.concerns.indexOf('boredom') >= 0) parts.push('indoor lifestyle');
    var isAdultAge = cat.age === 'senior' || cat.age === 'mature';
    if (isAdultAge) parts.push('age');
    else if (cat.appetite === 'picky') parts.push('picky appetite');
    else if (cat.age === 'kitten') parts.push('young age');
    if (!isAdultAge && (cat.anxiety === 'moderate' || cat.anxiety === 'high')) parts.push('temperament');

    if (parts.length > 0) {
      return 'Based on ' + name + "'s " + parts.join(' and ') + '.';
    }
    return 'Personalized for ' + name + '.';
  }

  function fixPluralReasonText(text) {
    return String(text || '')
      .replace(/Your cats deals/g, 'Your cats deal')
      .replace(/Your cats has/g, 'Your cats have')
      .replace(/Your cats is/g, 'Your cats are')
      .replace(/Your cats was/g, 'Your cats were');
  }

  function getAnalyzingFacts(max) {
    var facts = [];
    var cat = state.cats[0];
    if (!cat) return facts;

    if (cat.appetite === 'picky') facts.push(getFactPickyText());
    if (cat.coat === 'long') facts.push(CONFIG.factCoat || 'Long-haired cats swallow more fur when they groom.');
    if (cat.concerns.indexOf('hairballs') >= 0) facts.push(CONFIG.factHairballs || 'All three grasses help with hairballs.');
    if (cat.concerns.indexOf('digestion') >= 0) facts.push(CONFIG.factDigestion || 'If your cat has a health condition, check with your vet before adding anything new.');
    if (cat.concerns.indexOf('boredom') >= 0 || cat.concerns.indexOf('plants') >= 0) facts.push(CONFIG.factBoredom || 'Indoor cats retain natural foraging instincts.');
    if (cat.experience === 'first_time') facts.push(CONFIG.factFirstTimer || 'Most cats nibble grass, and most are healthy when they do.');
    if (cat.anxiety === 'moderate' || cat.anxiety === 'high') facts.push('A fresh box gives your cat something of their own to return to, every day.');

    if (facts.length === 0) facts.push('Harvested the morning after you order and delivered next day across the UAE.');
    return facts.slice(0, max);
  }

  function getDisplayProductTitle(productKey, fallback) {
    var names = {
      barley: 'Organic Barley Grass Box',
      wheat: 'Organic Wheat Grass Box',
      oat: 'Organic Oat Grass Box',
      bundle_complete: 'Complete Wellness Bundle',
      bundle_digestion: 'Weekly Digestion Duo',
      bundle_vitality: 'Weekly Vitality Pair',
      bundle_nourishment: 'Weekly Nourishment Mix'
    };
    return names[productKey] || fallback || productKey || '';
  }

  function getSocialProofText() {
    var social = CONFIG.socialProof || '';
    if (!social || /4,?500/.test(social)) return '10,000+ cats served across the UAE';
    return social;
  }

  function getFactPickyText() {
    var fact = CONFIG.factPicky || '';
    if (!fact || /Wheat grass.*softest/i.test(fact)) {
      return 'Oat is crisp grass for cats who are particular about what they eat.';
    }
    return fact;
  }

  function getProductPills(rec) {
    if (!rec) return '';

    var product = rec.product;
    var rule = rec.ruleId || '';
    var main = '';
    var styleClass = 'quiz-v2__product-pill--wheat';

    if (product === 'barley' || product === 'bundle_vitality') styleClass = 'quiz-v2__product-pill--barley';
    if (product === 'oat' || product === 'bundle_digestion' || product === 'bundle_nourishment') styleClass = 'quiz-v2__product-pill--oat';

    switch (rule) {
      case 'rule_digestion':
      case 'rule_2cat_digestion':
      case 'rule_3plus_digestion':
      case 'rule_3plus_digestion_bundle':
        main = 'Matched to your answers';
        break;
      case 'rule_hairball_acceptance':
        main = 'Helps with hairballs, for particular eaters';
        break;
      case 'rule_hairball_longcoat':
      case 'rule_hairball_senior':
      case 'rule_hairball':
      case 'rule_2cat_hairball':
      case 'rule_3plus_hairball':
        main = 'Helps with hairballs';
        break;
      case 'rule_hairball_picky':
        main = 'Hairball help for particular eaters';
        break;
      case 'rule_anxiety_acceptance':
      case 'rule_2cat_anxiety_acceptance':
      case 'rule_anxiety_shy_picky':
      case 'rule_picky':
      case 'rule_2cat_picky':
      case 'rule_2cat_picky_senior':
        main = 'For particular eaters';
        break;
      case 'rule_anxiety_foraging':
      case 'rule_3plus_anxiety':
        main = 'A daily ritual';
        break;
      case 'rule_boredom_barley':
      case 'rule_boredom_picky':
      case 'rule_3plus_enrichment':
      case 'rule_3plus_barley':
        main = 'Indoor enrichment support';
        break;
      case 'rule_kitten':
      case 'rule_kitten_picky':
        main = 'A soft first grass';
        break;
      case 'rule_tried_disliked':
        main = 'Worth another try';
        break;
      case 'rule_loves_grass':
        main = 'Best for grass lovers';
        break;
      case 'rule_senior_default':
        main = 'For adult and senior cats';
        break;
      case 'rule_long_coat_default':
        main = 'Helps with hairballs';
        break;
      case 'rule_3plus':
      case 'rule_2cat_default':
      case 'rule_3plus_default_a':
      case 'rule_3plus_default_b':
      case 'rule_3plus_vitamins':
        main = 'Variety for every cat';
        break;
      case 'rule_2cat_hairball_picky':
        main = 'Hairball + picky-cat coverage';
        break;
      default:
        main = 'A daily ritual';
        break;
    }

    return '<div class="quiz-v2__product-pills"><span class="quiz-v2__product-pill ' + styleClass + '">' + esc(main) + '</span></div>';
  }

  function ageFact(cat) {
    if (cat.age === 'kitten') return factBox(CONFIG.factFirstTimer || 'Most cats nibble grass, and most are healthy when they do.');
    if (cat.age === 'senior' || cat.age === 'mature') return factBox(CONFIG.factDigestion || 'If your cat has a health condition, check with your vet before adding anything new, grass included.');
    return '';
  }

  function currentStepHasFact() {
    var cat = state.cats[state.activeCat];
    if (!cat) return false;
    var concerns = cat.concerns || [];

    switch (state.step) {
      case STEPS.AGE:
        return cat.age === 'kitten' || cat.age === 'mature' || cat.age === 'senior';
      case STEPS.APPETITE:
        return cat.appetite === 'picky';
      case STEPS.COAT:
        return cat.coat === 'long';
      case STEPS.CONCERNS:
        return concerns.indexOf('hairballs') >= 0 ||
          concerns.indexOf('boredom') >= 0 ||
          concerns.indexOf('plants') >= 0 ||
          cat.coat === 'long';
      case STEPS.EXPERIENCE:
        return cat.experience === 'first_time';
      default:
        return false;
    }
  }

  function getFactReadDelay() {
    var configuredDelay = parseInt(CONFIG.factReadDelay, 10);
    if (configuredDelay >= 800 && configuredDelay <= 5000) return configuredDelay;
    return 2200;
  }

  function getAutoAdvanceDelay() {
    return currentStepHasFact() ? getFactReadDelay() : 120;
  }

  function clearPendingAutoAdvance() {
    if (!autoAdvanceTimer) return;
    clearTimeout(autoAdvanceTimer);
    autoAdvanceTimer = null;
  }

  function concernsFact(cat) {
    if (cat.concerns.indexOf('hairballs') >= 0) return factBox(CONFIG.factHairballs || 'All three grasses help with hairballs, so you can choose the grass that suits your cat.');
    if (cat.concerns.indexOf('boredom') >= 0 || cat.concerns.indexOf('plants') >= 0) return factBox(CONFIG.factBoredom || 'Indoor cats retain natural foraging instincts. Fresh grass provides safe sensory enrichment.');
    if (cat.coat === 'long') return factBox(CONFIG.factCoat || 'Long-haired cats swallow more fur when they groom. Fresh grass helps with hairballs.');
    return '';
  }

  // ─── OPTION HTML BUILDERS ─────────────────────────────────

  function ageOption(val, icon, label, sub, current) {
    var sel = current === val ? ' quiz-v2__option--selected' : '';
    return '<button class="quiz-v2__option' + sel + '" data-action="select_age" data-value="' + val + '">' +
      '<span class="quiz-v2__option-icon">' + icon + '</span>' +
      '<span class="quiz-v2__option-label">' + esc(label) + '</span>' +
      '<span class="quiz-v2__option-desc">' + esc(sub) + '</span></button>';
  }

  function appetiteOption(val, icon, label, current) {
    var sel = current === val ? ' quiz-v2__option--selected' : '';
    return '<button class="quiz-v2__option' + sel + '" data-action="select_appetite" data-value="' + val + '">' +
      '<span class="quiz-v2__option-icon">' + icon + '</span>' +
      '<span class="quiz-v2__option-label">' + esc(label) + '</span></button>';
  }

  function dietOption(val, icon, label, current) {
    var sel = current === val ? ' quiz-v2__option--selected' : '';
    return '<button class="quiz-v2__option' + sel + '" data-action="select_diet" data-value="' + val + '">' +
      '<span class="quiz-v2__option-icon">' + icon + '</span>' +
      '<span class="quiz-v2__option-label">' + esc(label) + '</span></button>';
  }

  function coatOption(val, icon, label, current) {
    var sel = current === val ? ' quiz-v2__option--selected' : '';
    return '<button class="quiz-v2__option' + sel + '" data-action="select_coat" data-value="' + val + '">' +
      '<span class="quiz-v2__option-icon">' + icon + '</span>' +
      '<span class="quiz-v2__option-label">' + esc(label) + '</span></button>';
  }

  function experienceOption(val, icon, label, current) {
    var sel = current === val ? ' quiz-v2__option--selected' : '';
    return '<button class="quiz-v2__option' + sel + '" data-action="select_experience" data-value="' + val + '">' +
      '<span class="quiz-v2__option-icon">' + icon + '</span>' +
      '<span class="quiz-v2__option-label">' + esc(label) + '</span></button>';
  }

  function listOption(val, icon, label, desc, current, actionName) {
    var sel = current === val ? ' quiz-v2__list-option--selected' : '';
    return '<button class="quiz-v2__list-option' + sel + '" data-action="' + actionName + '" data-value="' + val + '">' +
      '<span class="quiz-v2__list-option-icon">' + icon + '</span>' +
      '<div class="quiz-v2__list-option-content">' +
        '<span class="quiz-v2__list-option-label">' + esc(label) + '</span>' +
        '<span class="quiz-v2__list-option-desc">' + esc(desc) + '</span>' +
      '</div>' +
    '</button>';
  }

  function concernCheckbox(option, selected) {
    var isSelected = selected.indexOf(option.value) >= 0;
    var sel = isSelected ? ' quiz-v2__checkbox--selected' : '';
    return '<button class="quiz-v2__checkbox' + sel + '" data-action="toggle_concern" data-value="' + option.value + '">' +
      '<span class="quiz-v2__checkbox-box"><svg class="quiz-v2__checkbox-check" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"></polyline></svg></span>' +
      '<span class="quiz-v2__checkbox-icon">' + option.icon + '</span>' +
      '<span class="quiz-v2__checkbox-text">' + esc(option.label) + '</span></button>';
  }

  function factBox(text) {
    return '<div class="quiz-v2__fact" data-q2-fact role="status" aria-live="polite"><span class="quiz-v2__fact-icon" aria-hidden="true">\uD83D\uDCA1</span><span class="quiz-v2__fact-text">' + esc(text) + '</span></div>';
  }

  function reviewHtml(text, name, stars) {
    var starsStr = '';
    for (var i = 0; i < (stars || 5); i++) starsStr += '\u2605';
    return '<div class="quiz-v2__review">' +
      '<div class="quiz-v2__review-stars">' + starsStr + '</div>' +
      '<div class="quiz-v2__review-text">"' + esc(text) + '"</div>' +
      '<div class="quiz-v2__review-author">' + esc(name) + '</div></div>';
  }

  function emailCaptureHtml(name) {
    return '<div class="quiz-v2__email-capture">' +
      '<div class="quiz-v2__email-heading">' + esc(CONFIG.emailHeading || 'Save ' + name + "'s plan + get 10% off") + '</div>' +
      '<div class="quiz-v2__email-sub">' + esc(CONFIG.emailSub || "We'll send your personalized recommendation and a care guide.") + '</div>' +
      '<div class="quiz-v2__email-form quiz-v2__email-form--stacked">' +
        '<input class="quiz-v2__email-input" type="text" data-input="first_name" placeholder="Your first name" autocomplete="given-name" value="' + esc(state.firstName) + '" />' +
        '<input class="quiz-v2__email-input" type="email" data-input="email" placeholder="Your email" autocomplete="email" value="' + esc(state.email) + '" />' +
        '<label class="quiz-v2__meta-consent">' +
          '<input class="quiz-v2__meta-consent-input" type="checkbox" data-input="meta_ad_measurement"' + (state.metaAdMeasurementChecked ? ' checked' : '') + ' />' +
          '<span class="quiz-v2__meta-consent-copy">' + esc(META_AD_DISCLOSURE_COPY) + '</span>' +
        '</label>' +
        '<button class="quiz-v2__email-submit" data-action="submit_email">Send my plan</button>' +
      '</div>' +
      '<button class="quiz-v2__email-skip" data-action="skip_email">Skip</button></div>';
  }

  function trustIcon() {
    return '<svg class="quiz-v2__trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>';
  }

  function checkIcon() {
    return '<svg class="quiz-v2__benefit-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>';
  }

  function esc(str) {
    if (!str) return '';
    var d = document.createElement('div');
    d.textContent = String(str);
    return d.innerHTML;
  }

  // ─── BOOT ─────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
